import React, { useState, useEffect, useRef } from 'react';
import { useAuth } from '../context/AuthContext';
import API from '../utils/api';
import Swal from 'sweetalert2';
import * as XLSX from 'xlsx';
import { jsPDF } from 'jspdf';
import autoTable from 'jspdf-autotable';
import logoImage from '../assets/images/logo.png';
import bgImage from '../assets/images/bg.jpg';
import './Recruiter.css';

const VIOLATION_LABELS = {
  tab_change: 'Pindah Tab',
  window_blur: 'Keluar Jendela',
  face_missing: 'Wajah Tidak Terdeteksi',
  face_multiple: 'Banyak Wajah',
  copy_attempt: 'Copy-Paste',
  fullscreen_exit: 'Keluar Fullscreen'
};

const loadImageAsDataUrl = (src) => new Promise((resolve, reject) => {
  const image = new Image();
  image.crossOrigin = 'anonymous';
  image.onload = () => {
    try {
      const canvas = document.createElement('canvas');
      canvas.width = image.naturalWidth;
      canvas.height = image.naturalHeight;
      const context = canvas.getContext('2d');
      context.drawImage(image, 0, 0);
      resolve(canvas.toDataURL('image/png'));
    } catch (error) {
      reject(error);
    }
  };
  image.onerror = reject;
  image.src = src;
});

let tutorialAssetsPromise = null;

const getTutorialAssets = () => {
  if (!tutorialAssetsPromise) {
    tutorialAssetsPromise = Promise.all([
      loadImageAsDataUrl(logoImage),
      loadImageAsDataUrl(bgImage),
    ]).then(([logoDataUrl, bgDataUrl]) => ({ logoDataUrl, bgDataUrl }));
  }
  return tutorialAssetsPromise;
};

const renderTutorialCoverPage = (doc, assets, config) => {
  const pageWidth = doc.internal.pageSize.getWidth();
  const pageHeight = doc.internal.pageSize.getHeight();
  const accent = config.accent || [13, 122, 122];
  const title = config.title || 'Panduan';
  const subtitle = config.subtitle || '';
  const bullets = Array.isArray(config.bullets) ? config.bullets.slice(0, 4) : [];

  doc.setFillColor(247, 250, 252);
  doc.rect(0, 0, pageWidth, pageHeight, 'F');

  doc.setFillColor(accent[0], accent[1], accent[2]);
  doc.rect(0, 0, pageWidth, 48, 'F');

  if (assets?.logoDataUrl) {
    doc.addImage(assets.logoDataUrl, 'PNG', 14, 10, 62, 22);
  }
  doc.setTextColor(255, 255, 255);
  doc.setFontSize(10);
  doc.setFont('helvetica', 'bold');
  doc.text(config.kicker || 'Panduan penggunaan dashboard', 14, 38);

  if (assets?.bgDataUrl) {
    doc.addImage(assets.bgDataUrl, 'PNG', pageWidth - 66, 9, 52, 69);
  }

  doc.setFillColor(255, 255, 255);
  doc.roundedRect(14, 58, pageWidth - 28, 54, 4, 4, 'F');
  doc.setTextColor(15, 23, 42);
  doc.setFontSize(20);
  doc.setFont('helvetica', 'bold');
  doc.text(title, 20, 76);
  doc.setFontSize(11);
  doc.setFont('helvetica', 'normal');
  const subtitleLines = doc.splitTextToSize(subtitle, pageWidth - 40);
  doc.text(subtitleLines, 20, 87);

  doc.setFillColor(255, 255, 255);
  doc.roundedRect(14, 122, pageWidth - 28, 82, 4, 4, 'F');
  doc.setFillColor(accent[0], accent[1], accent[2]);
  doc.roundedRect(20, 128, 60, 12, 3, 3, 'F');
  doc.setTextColor(255, 255, 255);
  doc.setFontSize(10);
  doc.setFont('helvetica', 'bold');
  doc.text('Yang akan dipelajari', 24, 136);

  doc.setTextColor(31, 41, 55);
  doc.setFontSize(11);
  doc.setFont('helvetica', 'normal');
  let bulletY = 147;
  bullets.forEach((item) => {
    const lines = doc.splitTextToSize(item, pageWidth - 50);
    doc.text(`• ${lines[0]}`, 20, bulletY);
    if (lines.length > 1) {
      doc.text(lines.slice(1), 26, bulletY + 5);
      bulletY += (lines.length * 5) + 2;
    } else {
      bulletY += 9;
    }
  });

  doc.setTextColor(75, 85, 99);
  doc.setFontSize(9);
  doc.text(config.footer || 'Dokumen ini dibuat langsung dari halaman Recruiter/Admin.', 14, 286);
};

export default function RecruiterDashboard() {
  const { user, logout } = useAuth();
  const userMenuRef = useRef(null);
  
  // Tab state
  const [activeTab, setActiveTab] = useState('candidates');
  
  // Candidates/Sessions
  const [sessions, setSessions] = useState([]);
  const [selected, setSelected] = useState(null);
  const [detail, setDetail] = useState(null);
  const [loadingSessions, setLoadingSessions] = useState(true);
  const [detailLoading, setDetailLoading] = useState(false);
  const [filter, setFilter] = useState('all');
  const [candidateSearch, setCandidateSearch] = useState('');
  const [showCandidateDetailModal, setShowCandidateDetailModal] = useState(false);
  const [candidateDetail, setCandidateDetail] = useState(null);
  const [candidateDetailLoading, setCandidateDetailLoading] = useState(false);
  const [showSessionDetailModal, setShowSessionDetailModal] = useState(false);
  const [sessionDetail, setSessionDetail] = useState(null);
  const [sessionDetailLoading, setSessionDetailLoading] = useState(false);
  const [showPsychotestDetailModal, setShowPsychotestDetailModal] = useState(false);
  const [psychotestDetail, setPsychotestDetail] = useState(null);
  const [editingCandidateId, setEditingCandidateId] = useState(null);
  const [showExamQuestionsModal, setShowExamQuestionsModal] = useState(false);
  const [examModalExam, setExamModalExam] = useState(null);
  const [showUserMenu, setShowUserMenu] = useState(false);
  const [showChangePasswordModal, setShowChangePasswordModal] = useState(false);
  const [changingPassword, setChangingPassword] = useState(false);
  const [passwordForm, setPasswordForm] = useState({
    currentPassword: '',
    newPassword: '',
    confirmPassword: ''
  });
  const [recruiters, setRecruiters] = useState([]);
  const [loadingRecruiters, setLoadingRecruiters] = useState(false);
  const [showRecruiterForm, setShowRecruiterForm] = useState(false);
  const [editingRecruiterId, setEditingRecruiterId] = useState(null);
  const [recruiterForm, setRecruiterForm] = useState({
    name: '',
    email: '',
    password: ''
  });
  
  // Exams management
  const [exams, setExams] = useState([]);
  const [loadingExams, setLoadingExams] = useState(true);
  const [showExamForm, setShowExamForm] = useState(false);
  const [editingExam, setEditingExam] = useState(null);
  const [examForm, setExamForm] = useState({ 
    title: '', 
    description: '', 
    duration_minutes: 60, 
    max_violations: 1,
    total: 0,
    multiple_choice: 1,
    passing_grade: 60,
    position: 'all'
  });
  
  // Questions management for selected exam
  const [selectedExamForQuestions, setSelectedExamForQuestions] = useState(null);
  const [expandedExams, setExpandedExams] = useState({});
  const [questions, setQuestions] = useState([]);
  const [loadingQuestions, setLoadingQuestions] = useState(false);
  const [showQuestionForm, setShowQuestionForm] = useState(false);
  const [editingQuestion, setEditingQuestion] = useState(null);
  const [questionForm, setQuestionForm] = useState({
    question_text: '',
    option_a: '',
    option_b: '',
    option_c: '',
    option_d: '',
    option_e: '',
    correct_answer: 'a',
    points: 10,
    photo: null
  });
  const [questionPhotoFile, setQuestionPhotoFile] = useState(null);
  const [questionPhotoPreview, setQuestionPhotoPreview] = useState(null);
  const [selectedQuestionIds, setSelectedQuestionIds] = useState([]);
  const [selectAllQuestions, setSelectAllQuestions] = useState(false);
  const [excelFile, setExcelFile] = useState(null);
  
  // Pagination
  const [candidatePage, setCandidatePage] = useState(1);
  const [candidatePerPage, setCandidatePerPage] = useState('10');
  const [questionPage, setQuestionPage] = useState(1);
  const [questionPerPage, setQuestionPerPage] = useState('10');
  const [detailExamPage, setDetailExamPage] = useState(1);
  const [detailExamPerPage, setDetailExamPerPage] = useState('10');
  
  // Upload instructions modal
  const [showUploadInstructions, setShowUploadInstructions] = useState(false);
  const [uploadInstructionsType, setUploadInstructionsType] = useState('multiple-choice'); // 'multiple-choice' or 'essay'
  
  // Candidate registration
  const [showCandidateForm, setShowCandidateForm] = useState(false);
  const [candidateForm, setCandidateForm] = useState({
    name: '',
    email: '',
    password: '',
    position: 'general'
  });
  const [registering, setRegistering] = useState(false);

  // Psychotest management
  const [psychotestQuestions, setPsychotestQuestions] = useState([]);
  const [loadingPsychotest, setLoadingPsychotest] = useState(true);
  const [showPsychotestForm, setShowPsychotestForm] = useState(false);
  const [editingPsychotestQuestion, setEditingPsychotestQuestion] = useState(null);
  const [psychotestExcelFile, setPsychotestExcelFile] = useState(null);
  const [psychotestPage, setPsychotestPage] = useState(1);
  const [psychotestPerPage, setPsychotestPerPage] = useState('10');
  const [selectedPsychotestQuestionIds, setSelectedPsychotestQuestionIds] = useState([]);
  const [selectAllPsychotestQuestions, setSelectAllPsychotestQuestions] = useState(false);
  const [psychotestDurationMinutes, setPsychotestDurationMinutes] = useState(30);
  const [psychotestTotalQuestions, setPsychotestTotalQuestions] = useState(0);
  const [psychotestMaxViolations, setPsychotestMaxViolations] = useState(3);
  const [savingPsychotestDuration, setSavingPsychotestDuration] = useState(false);
  const [psychotestForm, setPsychotestForm] = useState({
    dimension_a: 'extro_intro',
    statement_a: '',
    dimension_b: 'emotional_stability',
    statement_b: '',
    is_active: 1,
  });

  const PSY_DIMENSIONS = [
    { value: 'extro_intro', label: 'Ekstroversi vs Introversi' },
    { value: 'emotional_stability', label: 'Stabilitas Emosi' },
    { value: 'openness_innovation', label: 'Keterbukaan & Inovasi' },
    { value: 'agreeableness_collaboration', label: 'Keramahan & Kolaborasi' },
    { value: 'integrity_work_ethic', label: 'Integritas & Etika Kerja' },
  ];

  const buildUploadUrl = (folder, fileName) => {
    if (!fileName) return '';
    const value = String(fileName);
    if (/^https?:\/\//i.test(value) || value.startsWith('/')) {
      return value;
    }
    return `/uploads/${folder}/${value}`;
  };

  const parsePsychotestResult = (result) => {
    if (!result) return null;
    if (typeof result === 'string') {
      try {
        return JSON.parse(result);
      } catch (e) {
        return null;
      }
    }
    return result;
  };

  const formatPsychotestResult = (result) => {
    const parsed = parsePsychotestResult(result);
    if (!parsed) {
      return { summary: '-', dimensions: [] };
    }

    if (parsed.status === 'disqualified') {
      return {
        summary: `Diskualifikasi${parsed.reason ? ` - ${parsed.reason}` : ''}`,
        dimensions: [],
      };
    }

    const dimensions = Array.isArray(parsed.dimensions) ? parsed.dimensions : [];
    const strongest = parsed.summary?.strongest_dimension || dimensions.slice().sort((a, b) => (b.score || 0) - (a.score || 0))[0];
    const weakest = parsed.summary?.needs_attention_dimension || dimensions.slice().sort((a, b) => (a.score || 0) - (b.score || 0))[0];
    const parts = [];

    if (strongest) {
      parts.push(`Terkuat: ${strongest.title || strongest.key || '-'}${strongest.score != null ? ` (${strongest.score}%)` : ''}`);
    }
    if (weakest) {
      parts.push(`Perlu perhatian: ${weakest.title || weakest.key || '-'}${weakest.score != null ? ` (${weakest.score}%)` : ''}`);
    }

    return {
      summary: parts.length > 0 ? parts.join(' | ') : 'Hasil tersedia',
      dimensions,
    };
  };

  // ─── Load Sessions ──────────────────────────────────────────────────────────
  useEffect(() => {
    const loadSessions = async () => {
      try {
        const res = await API.get('/recruiter/sessions');
        setSessions(res.data);
      } catch (err) {
        console.error(err);
      } finally {
        setLoadingSessions(false);
      }
    };
    loadSessions();
  }, []);

  // ─── Load Exams ─────────────────────────────────────────────────────────────
  useEffect(() => {
    if (activeTab !== 'exams') return;
    const loadExams = async () => {
      setLoadingExams(true);
      try {
        const res = await API.get('/recruiter/exams');
        setExams(res.data);
      } catch (err) {
        console.error(err);
      } finally {
        setLoadingExams(false);
      }
    };
    loadExams();
  }, [activeTab]);

  useEffect(() => {
    if (activeTab !== 'psychotest') return;
    const loadPsychotest = async () => {
      setLoadingPsychotest(true);
      try {
        const [qRes, settingsRes] = await Promise.all([
          API.get('/recruiter/psychotest/questions'),
          API.get('/recruiter/psychotest/settings'),
        ]);
        setPsychotestQuestions(qRes.data);
        setPsychotestDurationMinutes(Number(settingsRes.data?.duration_minutes || 30));
        setPsychotestTotalQuestions(Number(settingsRes.data?.total_questions || 0));
        setPsychotestMaxViolations(Number(settingsRes.data?.max_violations || 3));
        setPsychotestPage(1);
        setSelectedPsychotestQuestionIds([]);
        setSelectAllPsychotestQuestions(false);
      } catch (err) {
        console.error(err);
      } finally {
        setLoadingPsychotest(false);
      }
    };
    loadPsychotest();
  }, [activeTab]);

  useEffect(() => {
    if (user?.role !== 'admin' || activeTab !== 'recruiters') return;
    const loadRecruiters = async () => {
      setLoadingRecruiters(true);
      try {
        const res = await API.get('/admin/recruiters');
        setRecruiters(res.data || []);
      } catch (err) {
        Swal.fire({ icon: 'error', title: 'Error', text: err.response?.data?.message || 'Gagal memuat data recruiter' });
      } finally {
        setLoadingRecruiters(false);
      }
    };
    loadRecruiters();
  }, [activeTab, user?.role]);

  useEffect(() => {
    const handleClickOutside = (event) => {
      if (userMenuRef.current && !userMenuRef.current.contains(event.target)) {
        setShowUserMenu(false);
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, []);

  // ─── Load Questions ─────────────────────────────────────────────────────────
  useEffect(() => {
    if (!selectedExamForQuestions) return;
    const loadQuestions = async () => {
      setLoadingQuestions(true);
      try {
        const res = await API.get(`/recruiter/exam/${selectedExamForQuestions}/questions`);
        setQuestions(res.data);
        setQuestionPage(1); // Reset to page 1 when questions loaded
      } catch (err) {
        console.error(err);
      } finally {
        setLoadingQuestions(false);
      }
    };
    loadQuestions();
  }, [selectedExamForQuestions]);

  useEffect(() => {
    setQuestionPage(1);
  }, [questionPerPage]);

  useEffect(() => {
    setPsychotestPage(1);
  }, [psychotestPerPage]);

  useEffect(() => {
    setCandidatePage(1);
  }, [candidatePerPage, candidateSearch, filter]);

  useEffect(() => {
    setDetailExamPage(1);
  }, [detailExamPerPage, candidateDetail]);

  // ─── Pagination ─────────────────────────────────────────────────────────────
  const resolvePageSize = (value, total) => (value === 'all' ? Math.max(total, 1) : Number(value));

  const effectiveQuestionPerPage = resolvePageSize(questionPerPage, questions.length);
  const totalPages = Math.max(1, Math.ceil(questions.length / effectiveQuestionPerPage));
  const paginatedQuestions = questions.slice(
    (questionPage - 1) * effectiveQuestionPerPage,
    questionPage * effectiveQuestionPerPage
  );

  // ─── Session Detail ─────────────────────────────────────────────────────────
  const viewDetail = async (sess) => {
    if (!sess?.id) {
      Swal.fire({ icon: 'info', title: 'Info', text: 'Peserta ini belum memiliki sesi ujian.' });
      return;
    }
    setSelected(sess);
    setDetail(null);
    setDetailLoading(true);
    try {
      const res = await API.get(`/recruiter/session/${sess.id}`);
      setDetail(res.data);
    } catch (e) {}
    setDetailLoading(false);
  };

  const openCandidateDetail = async (sess) => {
    setCandidateDetailLoading(true);
    setCandidateDetail(null);
    setDetailExamPage(1);
    setShowCandidateDetailModal(true);
    try {
      const res = await API.get(`/recruiter/candidate/${sess.candidate_id}`);
      setCandidateDetail(res.data);
      setEditingCandidateId(sess.candidate_id);
    } catch (err) {
      console.error(err);
      Swal.fire({ icon: 'error', title: 'Error', text: 'Gagal memuat detail kandidat' });
      setShowCandidateDetailModal(false);
    } finally {
      setCandidateDetailLoading(false);
    }
  };

  const openSessionAnswerDetail = async (examSession) => {
    if (!examSession?.session_id) {
      Swal.fire({ icon: 'info', title: 'Info', text: 'Detail jawaban belum tersedia untuk sesi ini.' });
      return;
    }

    setSessionDetailLoading(true);
    setSessionDetail(null);
    setShowSessionDetailModal(true);
    try {
      const res = await API.get(`/recruiter/session/${examSession.session_id}`);
      setSessionDetail(res.data);
    } catch (err) {
      console.error(err);
      Swal.fire({ icon: 'error', title: 'Error', text: 'Gagal memuat detail jawaban ujian' });
      setShowSessionDetailModal(false);
    } finally {
      setSessionDetailLoading(false);
    }
  };

  const openPsychotestAttemptDetail = (attempt) => {
    setPsychotestDetail(attempt || null);
    setShowPsychotestDetailModal(true);
  };

  const closePsychotestDetailModal = () => {
    setShowPsychotestDetailModal(false);
    setPsychotestDetail(null);
  };

  const resetCandidateExamData = async () => {
    if (!candidateDetail?.candidate?.id) {
      Swal.fire({ icon: 'info', title: 'Info', text: 'Detail kandidat belum tersedia' });
      return;
    }

    const result = await Swal.fire({
      icon: 'warning',
      title: 'Reset Data Exam?',
      text: 'Semua riwayat ujian, jawaban, foto sesi, dan pelanggaran kandidat ini akan dihapus. Kandidat bisa test ulang.',
      showCancelButton: true,
      confirmButtonText: 'Ya, Reset Exam',
      cancelButtonText: 'Batal',
      confirmButtonColor: '#ef4444',
    });

    if (!result.isConfirmed) return;

    try {
      await API.post(`/recruiter/candidate/${candidateDetail.candidate.id}/reset-exam`);
      Swal.fire({ icon: 'success', title: 'Berhasil', text: 'Data exam kandidat berhasil direset', timer: 1500, showConfirmButton: false });
      const [detailRes, sessionsRes] = await Promise.all([
        API.get(`/recruiter/candidate/${candidateDetail.candidate.id}`),
        API.get('/recruiter/sessions'),
      ]);
      setCandidateDetail(detailRes.data);
      setSessions(sessionsRes.data);
    } catch (err) {
      Swal.fire({ icon: 'error', title: 'Error', text: err.response?.data?.message || err.message });
    }
  };

  const resetCandidatePsychotestData = async () => {
    if (!candidateDetail?.candidate?.id) {
      Swal.fire({ icon: 'info', title: 'Info', text: 'Detail kandidat belum tersedia' });
      return;
    }

    const result = await Swal.fire({
      icon: 'warning',
      title: 'Reset Data Psikotest?',
      text: 'Status psikotest, hasil, riwayat attempt, dan foto awal psikotest kandidat ini akan dihapus. Kandidat bisa test ulang.',
      showCancelButton: true,
      confirmButtonText: 'Ya, Reset Psikotest',
      cancelButtonText: 'Batal',
      confirmButtonColor: '#ef4444',
    });

    if (!result.isConfirmed) return;

    try {
      await API.post(`/recruiter/candidate/${candidateDetail.candidate.id}/reset-psychotest`);
      Swal.fire({ icon: 'success', title: 'Berhasil', text: 'Data psikotest kandidat berhasil direset', timer: 1500, showConfirmButton: false });
      const [detailRes, sessionsRes] = await Promise.all([
        API.get(`/recruiter/candidate/${candidateDetail.candidate.id}`),
        API.get('/recruiter/sessions'),
      ]);
      setCandidateDetail(detailRes.data);
      setSessions(sessionsRes.data);
    } catch (err) {
      Swal.fire({ icon: 'error', title: 'Error', text: err.response?.data?.message || err.message });
    }
  };

  const openExamQuestionsModal = (exam) => {
    setExamModalExam(exam);
    setShowExamQuestionsModal(true);
    setQuestionPage(1);
    if (selectedExamForQuestions !== exam.id) {
      setSelectedExamForQuestions(exam.id);
    }
  };

  const downloadExcel = (rows, filename, sheetName = 'Data') => {
    if (!rows || rows.length === 0) {
      Swal.fire({ icon: 'info', title: 'Info', text: 'Tidak ada data untuk di-export' });
      return;
    }

    const worksheet = XLSX.utils.json_to_sheet(rows);
    const workbook = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(workbook, worksheet, sheetName);
    const dateSuffix = new Date().toISOString().slice(0, 10);
    XLSX.writeFile(workbook, `${filename}_${dateSuffix}.xlsx`);
  };

  const exportCandidatesExcel = () => {
    const rows = paginatedCandidates.map((s, idx) => {
      const scoreValue = Number(s.score || 0);
      const overallStatus = s.overall_status || 'in_progress';
      const status = overallStatus === 'lulus'
        ? 'Lulus'
        : overallStatus === 'tidak_lulus'
          ? 'Tidak Lulus'
          : 'In Progress';

      return {
        No: ((candidatePage - 1) * effectiveCandidatePerPage) + idx + 1,
        Nama: s.candidate_name || '-',
        Email: s.candidate_email || '-',
        Posisi: s.position || '-',
        Status: status,
        Nilai: overallStatus === 'in_progress' ? '-' : `${scoreValue}%`,
        Pelanggaran: s.violation_count || 0,
        Email_Hasil: s.result_email_sent_at ? 'Terkirim' : 'Belum',
      };
    });
    downloadExcel(rows, 'peserta_rekrutmen', 'Peserta');
  };

  const sendCandidateResultEmail = async (candidate) => {
    const candidateId = candidate?.candidate_id;
    if (!candidateId) return;

    if ((candidate.overall_status || 'in_progress') === 'in_progress') {
      Swal.fire({
        icon: 'info',
        title: 'Belum Bisa Dikirim',
        text: 'Status kandidat masih In Progress. Email hasil dapat dikirim setelah semua proses exam dan psikotest selesai.',
      });
      return;
    }

    try {
      const res = await API.post(`/recruiter/candidate/${candidateId}/send-result-email`);
      Swal.fire({
        icon: 'success',
        title: 'Email Terkirim',
        text: res.data?.message || 'Email hasil berhasil dikirim',
        timer: 1800,
        showConfirmButton: false,
      });

      const sessionsRes = await API.get('/recruiter/sessions');
      setSessions(sessionsRes.data);
    } catch (err) {
      Swal.fire({ icon: 'error', title: 'Error', text: err.response?.data?.message || err.message });
    }
  };

  const exportCandidateExamHistoryExcel = () => {
    const rows = paginatedCandidateDetailExams.map((exam, idx) => ({
      No: ((detailExamPage - 1) * effectiveDetailExamPerPage) + idx + 1,
      Ujian: exam.exam_title || '-',
      Status: exam.is_disqualified ? 'Diskualifikasi' : exam.status === 'active' ? 'Sedang Ujian' : exam.status === 'finished' ? 'Selesai' : exam.status,
      Nilai: exam.score != null ? `${exam.score}%` : '-',
      Pelanggaran: exam.violation_count || 0,
      Foto: exam.session_photo ? 'Ada' : '-',
      Waktu: exam.started_at ? new Date(exam.started_at).toLocaleString('id-ID') : '-'
    }));
    downloadExcel(rows, 'riwayat_ujian_kandidat', 'Riwayat Ujian');
  };

  const exportExamQuestionsExcel = () => {
    const rows = paginatedQuestions.map((q, idx) => ({
      No: ((questionPage - 1) * effectiveQuestionPerPage) + idx + 1,
      Pertanyaan: q.question_text || '-',
      Tipe: q.correct_answer ? 'Pilihan Ganda' : 'Essay',
      Opsi_A: q.option_a || '-',
      Opsi_B: q.option_b || '-',
      Opsi_C: q.option_c || '-',
      Opsi_D: q.option_d || '-',
      Opsi_E: q.option_e || '-',
      Jawaban_Benar: q.correct_answer || '-',
      Rubrik: !q.correct_answer ? (q.option_a || '-') : '-',
      Poin: q.points || 0
    }));
    downloadExcel(rows, 'bank_soal_ujian', 'Soal Ujian');
  };

  const exportPsychotestExcel = () => {
    const rows = paginatedPsychotestQuestions.map((q) => ({
      ID: q.id,
      Dimensi_A: PSY_DIMENSIONS.find((d) => d.value === q.dimension_a)?.label || q.dimension_a,
      Pernyataan_A: q.statement_a || '-',
      Dimensi_B: PSY_DIMENSIONS.find((d) => d.value === q.dimension_b)?.label || q.dimension_b,
      Pernyataan_B: q.statement_b || '-',
      Status: Number(q.is_active) === 1 ? 'Aktif' : 'Nonaktif'
    }));
    downloadExcel(rows, 'soal_psikotest', 'Psikotest');
  };

  const downloadCandidateDetailPdf = () => {
    if (!candidateDetail) {
      Swal.fire({ icon: 'info', title: 'Info', text: 'Detail kandidat belum tersedia' });
      return;
    }

    const doc = new jsPDF();
    const c = candidateDetail.candidate || {};
    const exams = candidateDetail.exams || [];
    const psycho = candidateDetail.psychotest_attempts || [];
    const psychotestSummary = formatPsychotestResult(c.psychotest_result || psycho.find((attempt) => attempt.status === 'completed' && attempt.result)?.result);
    const psychotestSummaryLines = doc.splitTextToSize(`Hasil Psikotest: ${psychotestSummary.summary}`, 180);
    const examsTableStartY = 50 + (psychotestSummaryLines.length * 6) + 6;

    doc.setFontSize(16);
    doc.text('Detail Kandidat', 14, 16);
    doc.setFontSize(11);
    doc.text(`Nama: ${c.name || '-'}`, 14, 26);
    doc.text(`Email: ${c.email || '-'}`, 14, 32);
    doc.text(`Posisi: ${c.position || '-'}`, 14, 38);
    doc.text(`Psikotest: ${c.psychotest_completed ? 'Selesai' : 'Belum'}`, 14, 44);
    doc.text(psychotestSummaryLines, 14, 50);

    autoTable(doc, {
      startY: examsTableStartY,
      head: [['No', 'Ujian', 'Foto', 'Status', 'Nilai', 'Pelanggaran', 'Waktu']],
      body: exams.length
        ? exams.map((exam, idx) => ([
            idx + 1,
            exam.exam_title || '-',
            exam.session_photo ? 'Ada' : '-',
            exam.is_disqualified ? 'Diskualifikasi' : exam.status === 'active' ? 'Sedang Ujian' : exam.status === 'finished' ? 'Selesai' : exam.status,
            exam.score != null ? `${exam.score}%` : '-',
            exam.violation_count || 0,
            exam.started_at ? new Date(exam.started_at).toLocaleString('id-ID') : '-'
          ]))
        : [['-', 'Tidak ada riwayat ujian', '-', '-', '-', '-', '-']]
    });

    autoTable(doc, {
      startY: doc.lastAutoTable.finalY + 10,
      head: [['No', 'Status', 'Mulai', 'Selesai', 'Hasil']],
      body: psycho.length
        ? psycho.map((attempt, idx) => ([
            idx + 1,
            attempt.status === 'completed' ? 'Selesai' : attempt.status === 'active' ? 'Sedang Berlangsung' : attempt.status,
            attempt.started_at ? new Date(attempt.started_at).toLocaleString('id-ID') : '-',
            attempt.completed_at ? new Date(attempt.completed_at).toLocaleString('id-ID') : '-',
            attempt.status === 'completed' ? formatPsychotestResult(attempt.result).summary : (attempt.result ? 'Hasil tersedia' : 'Belum ada hasil')
          ]))
        : [['-', 'Tidak ada riwayat psikotest', '-', '-', '-']]
    });

    const safeName = (c.name || 'kandidat').replace(/[^a-zA-Z0-9-_]/g, '_');
    doc.save(`detail_kandidat_${safeName}.pdf`);
  };

  const downloadSessionDetailPdf = () => {
    if (!sessionDetail) {
      Swal.fire({ icon: 'info', title: 'Info', text: 'Detail jawaban belum tersedia' });
      return;
    }

    const doc = new jsPDF();
    const session = sessionDetail.session || {};
    const answers = sessionDetail.answers || [];
    const violations = sessionDetail.violations || [];

    doc.setFontSize(16);
    doc.text('Detail Jawaban Ujian', 14, 16);
    doc.setFontSize(11);
    doc.text(`Kandidat: ${session.candidate_name || '-'}`, 14, 26);
    doc.text(`Email: ${session.candidate_email || '-'}`, 14, 32);
    doc.text(`Ujian: ${session.exam_title || '-'}`, 14, 38);
    doc.text(`Status: ${session.status || '-'}`, 14, 44);
    doc.text(`Skor: ${session.score != null ? `${session.score}%` : '-'}`, 14, 50);
    doc.text(`Foto sesi: ${session.photo ? 'Ada' : 'Tidak ada'}`, 14, 56);

    autoTable(doc, {
      startY: 64,
      head: [['No', 'Pertanyaan', 'Foto', 'Jawaban Kandidat', 'Kunci', 'Status']],
      body: answers.length
        ? answers.map((answer, idx) => ([
            idx + 1,
            answer.question_text || '-',
            answer.photo ? 'Ada' : '-',
            answer.correct_answer ? ((answer.selected_option || '-').toString().toUpperCase()) : (answer.selected_option || '-'),
            answer.correct_answer ? String(answer.correct_answer).toUpperCase() : '-',
            answer.correct_answer ? (Number(answer.is_correct) === 1 ? 'Benar' : 'Salah') : 'Essay',
          ]))
        : [['-', 'Tidak ada jawaban', '-', '-', '-', '-']]
    });

    if (violations.length > 0) {
      autoTable(doc, {
        startY: doc.lastAutoTable.finalY + 10,
        head: [['No', 'Pelanggaran', 'Deskripsi', 'Waktu']],
        body: violations.map((violation, idx) => ([
          idx + 1,
          VIOLATION_LABELS[violation.violation_type] || violation.violation_type || '-',
          violation.description || '-',
          violation.logged_at ? new Date(violation.logged_at).toLocaleString('id-ID') : '-'
        ]))
      });
    }

    const safeName = (session.candidate_name || 'sesi_ujian').replace(/[^a-zA-Z0-9-_]/g, '_');
    doc.save(`detail_jawaban_${safeName}.pdf`);
  };

  const downloadPsychotestDetailPdf = () => {
    if (!psychotestDetail) {
      Swal.fire({ icon: 'info', title: 'Info', text: 'Detail psikotest belum tersedia' });
      return;
    }

    const doc = new jsPDF();
    const statusLabel = psychotestDetail.status === 'completed'
      ? 'Selesai'
      : psychotestDetail.status === 'active'
        ? 'Sedang Berlangsung'
        : psychotestDetail.status === 'timeout'
          ? 'Timeout'
          : psychotestDetail.status === 'disqualified'
            ? 'Diskualifikasi'
            : psychotestDetail.status;
    const resultSummary = psychotestDetail.result ? formatPsychotestResult(psychotestDetail.result).summary : '-';

    doc.setFontSize(16);
    doc.text('Detail Psikotest', 14, 16);
    doc.setFontSize(11);
    doc.text(`Status: ${statusLabel}`, 14, 26);
    doc.text(`Mulai: ${psychotestDetail.started_at ? new Date(psychotestDetail.started_at).toLocaleString('id-ID') : '-'}`, 14, 32);
    doc.text(`Selesai: ${psychotestDetail.completed_at ? new Date(psychotestDetail.completed_at).toLocaleString('id-ID') : '-'}`, 14, 38);
    doc.text(doc.splitTextToSize(`Ringkasan: ${resultSummary}`, 180), 14, 44);

    const scoringExplanation = [
      'Cara penilaian:',
      '1. Setiap soal punya dua pernyataan: A dan B.',
      '2. Pilihan A menambah skor dimensi A, pilihan B menambah skor dimensi B.',
      '3. Rumus skor tiap dimensi: (selected / appearances) x 100.',
      '4. Hasil bukan benar/salah, tetapi kecenderungan kepribadian.',
      '5. >= 70% = kecenderungan tinggi, <= 45% = kecenderungan rendah, sisanya seimbang.',
      'Contoh sederhana:',
      'Jika dimensi Ekstrover muncul 3 kali pada soal, berarti appearances = 3.',
      'Kalau kandidat memilih sisi Ekstrover 2 kali, berarti selected = 2.',
      'Maka skor Ekstrover = (2 / 3) x 100 = 66,7%.',
    ];
    doc.setFontSize(10);
    doc.text(scoringExplanation, 14, 54);

    const answers = Array.isArray(psychotestDetail.answers) ? psychotestDetail.answers : [];

    autoTable(doc, {
      startY: 78,
      head: [['No', 'Dimensi A', 'Pernyataan A', 'Dimensi B', 'Pernyataan B', 'Jawaban']],
      body: answers.length
        ? answers.map((answer, idx) => ([
            idx + 1,
            answer.dimension_a || '-',
            answer.statement_a || '-',
            answer.dimension_b || '-',
            answer.statement_b || '-',
            String(answer.answer_value || '-').toUpperCase(),
          ]))
        : [['-', 'Tidak ada detail pertanyaan', '-', '-', '-', '-']]
    });

    const safeName = `psikotest_${psychotestDetail.id}`;
    doc.save(`detail_${safeName}.pdf`);
  };

  const downloadDashboardTutorialPdf = async () => {
    try {
      const assets = await getTutorialAssets();
      const doc = new jsPDF();
      const isAdmin = user?.role === 'admin';
      const marginLeft = 14;
      const pageWidth = doc.internal.pageSize.getWidth();
      const contentWidth = pageWidth - (marginLeft * 2);
      let cursorY = 16;

      renderTutorialCoverPage(doc, assets, {
        title: 'Panduan Recruiter Dashboard',
        subtitle: 'Panduan ini menjelaskan fungsi utama dashboard, pengelolaan kandidat, reset data, detail ujian, detail psikotest, dan fitur admin.',
        bullets: [
          'Kelola peserta, ujian, soal, dan psikotest dari satu tempat.',
          'Buka detail kandidat untuk melihat riwayat, foto, dan hasil tes.',
          'Gunakan reset data jika kandidat perlu test ulang.',
          isAdmin ? 'Admin bisa mengelola akun recruiter langsung dari dashboard.' : 'Akses admin akan muncul hanya untuk role admin.',
        ],
        accent: [13, 122, 122],
        kicker: 'Panduan penggunaan dashboard recruiter dan admin',
        footer: 'Dokumen ini dibuat langsung dari halaman Recruiter/Admin.',
      });

      doc.addPage();

      const addSectionTitle = (title) => {
        if (assets?.logoDataUrl) {
          doc.addImage(assets.logoDataUrl, 'PNG', marginLeft, cursorY - 1, 7, 7);
        }
        doc.setFontSize(14);
        doc.setFont('helvetica', 'bold');
        doc.text(title, marginLeft + 11, cursorY + 5);
        cursorY += 11;
      };

    const addParagraph = (text, size = 11) => {
      doc.setFontSize(size);
      doc.setFont('helvetica', 'normal');
      const lines = doc.splitTextToSize(text, contentWidth);
      doc.text(lines, marginLeft, cursorY);
      cursorY += lines.length * 5 + 2;
    };

    const addBullets = (items) => {
      doc.setFontSize(11);
      doc.setFont('helvetica', 'normal');
      items.forEach((item) => {
        const lines = doc.splitTextToSize(item, contentWidth - 6);
        doc.text(`• ${lines[0]}`, marginLeft, cursorY);
        if (lines.length > 1) {
          doc.text(lines.slice(1), marginLeft + 6, cursorY + 5);
          cursorY += (lines.length * 5);
        } else {
          cursorY += 5;
        }
      });
      cursorY += 2;
    };

    const ensureSpace = (needed) => {
      if (cursorY + needed > 275) {
        doc.addPage();
        cursorY = 16;
      }
    };

    doc.setFontSize(18);
    doc.setFont('helvetica', 'bold');
    doc.text('Panduan Recruiter Dashboard', marginLeft, cursorY);
    cursorY += 8;
    doc.setFontSize(11);
    doc.setFont('helvetica', 'normal');
    doc.text(`Role aktif: ${isAdmin ? 'Admin' : 'Recruiter'}`, marginLeft, cursorY);
    cursorY += 8;

    addSectionTitle('Gambaran Umum');
    addParagraph('Dashboard ini dipakai untuk mengelola peserta, ujian, soal, psikotest, serta recruiter (khusus admin). Semua detail kandidat juga bisa dibuka untuk melihat riwayat ujian, riwayat psikotest, dan melakukan reset data jika perlu test ulang.');

    ensureSpace(60);
    addSectionTitle('Fungsi Utama');
    autoTable(doc, {
      startY: cursorY,
      head: [['Fitur', 'Lokasi', 'Fungsi']],
      body: [
        ['Data Peserta', 'Tab Peserta Ujian', 'Cari peserta, lihat status, buka detail kandidat, dan export data.'],
        ['Detail Kandidat', 'Tombol Detail', 'Lihat riwayat ujian, riwayat psikotest, download PDF detail, reset exam, reset psikotest.'],
        ['Detail Ujian', 'Popup Detail Jawaban', 'Lihat foto sesi, pertanyaan, jawaban kandidat, dan pelanggaran.'],
        ['Kelola Ujian', 'Tab Kelola Ujian & Soal', 'Tambah, edit, hapus ujian dan bank soal, upload Excel, export Excel.'],
        ['Kelola Psikotest', 'Tab Kelola Psikotest', 'Tambah, edit, hapus soal psikotest, atur durasi, upload Excel, export Excel.'],
        [isAdmin ? 'Kelola Recruiter' : 'Akses Recruiter', 'Tab Admin', isAdmin ? 'Tambah, edit, hapus recruiter.' : 'Tab ini hanya muncul untuk admin.'],
      ].filter(Boolean),
      styles: { fontSize: 9, cellPadding: 3, valign: 'top' },
      headStyles: { fillColor: [13, 122, 122] },
      margin: { left: marginLeft, right: marginLeft },
    });
    cursorY = doc.lastAutoTable.finalY + 8;

    ensureSpace(70);
    addSectionTitle('Alur Singkat Pemakaian');
    addBullets([
      'Buka tab Peserta Ujian untuk mencari kandidat berdasarkan nama, email, atau posisi.',
      'Klik Detail pada kandidat untuk melihat riwayat ujian dan psikotest.',
      'Gunakan tombol Reset Exam atau Reset Psikotest jika kandidat perlu test ulang.',
      'Masuk ke tab Kelola Ujian & Soal untuk menambah ujian, upload soal Excel, atau export bank soal.',
      'Masuk ke tab Kelola Psikotest untuk mengatur durasi, jumlah soal, dan bank soal A/B.',
      isAdmin ? 'Jika Anda admin, buka tab Kelola Recruiter untuk mengelola akun recruiter.' : 'Jika Anda bukan admin, tab Kelola Recruiter tidak akan ditampilkan.',
    ]);

    ensureSpace(70);
    addSectionTitle('Cara Penilaian Psikotest');
    addParagraph('Penilaian psikotest memakai kecenderungan, bukan benar/salah. Setiap soal punya dua sisi: A dan B. Kalau kandidat memilih A, skor dimensi A bertambah. Kalau memilih B, skor dimensi B bertambah.');
    addParagraph('Rumusnya: skor dimensi = (selected / appearances) x 100. Contoh sederhana: jika dimensi Ekstrover muncul 3 kali lalu dipilih 2 kali, maka skornya 66,7%.', 10);
    addBullets([
      '>= 70% berarti kecenderungan tinggi pada dimensi tersebut.',
      '<= 45% berarti kecenderungan rendah pada dimensi tersebut.',
      'Nilai di tengah berarti hasilnya seimbang / sedang.',
    ]);

    ensureSpace(50);
    addSectionTitle('Tips Cepat');
    addBullets([
      'Gunakan download PDF detail untuk arsip kandidat.',
      'Gunakan export Excel jika ingin rekap data lebih cepat.',
      'Gunakan reset data hanya jika kandidat memang perlu test ulang.',
    ]);

      doc.save(`panduan_recruiter_dashboard_${isAdmin ? 'admin' : 'recruiter'}.pdf`);
    } catch (error) {
      Swal.fire({ icon: 'error', title: 'Error', text: error.message || 'Gagal membuat PDF tutorial' });
    }
  };

  const downloadExamProcedurePdf = () => {
    const doc = new jsPDF();
    const marginLeft = 14;
    const pageWidth = doc.internal.pageSize.getWidth();
    const contentWidth = pageWidth - (marginLeft * 2);
    let cursorY = 16;

    const addSectionTitle = (title) => {
      doc.setFontSize(14);
      doc.setFont('helvetica', 'bold');
      doc.text(title, marginLeft, cursorY);
      cursorY += 8;
    };

    const addParagraph = (text, size = 11) => {
      doc.setFontSize(size);
      doc.setFont('helvetica', 'normal');
      const lines = doc.splitTextToSize(text, contentWidth);
      doc.text(lines, marginLeft, cursorY);
      cursorY += lines.length * 5 + 2;
    };

    const addBullets = (items) => {
      doc.setFontSize(11);
      doc.setFont('helvetica', 'normal');
      items.forEach((item) => {
        const lines = doc.splitTextToSize(item, contentWidth - 6);
        doc.text(`• ${lines[0]}`, marginLeft, cursorY);
        if (lines.length > 1) {
          doc.text(lines.slice(1), marginLeft + 6, cursorY + 5);
          cursorY += lines.length * 5;
        } else {
          cursorY += 5;
        }
      });
      cursorY += 2;
    };

    const ensureSpace = (needed) => {
      if (cursorY + needed > 275) {
        doc.addPage();
        cursorY = 16;
      }
    };

    doc.setFontSize(18);
    doc.setFont('helvetica', 'bold');
    doc.text('Tata Cara Pengerjaan Exam', marginLeft, cursorY);
    cursorY += 8;
    addParagraph('Panduan ini menjelaskan langkah mengerjakan ujian, mulai dari masuk ke sesi, menyiapkan foto awal, menjawab soal, sampai selesai submit.');

    ensureSpace(50);
    addSectionTitle('Sebelum Memulai');
    addBullets([
      'Pastikan perangkat kamera dan koneksi internet stabil.',
      'Siapkan posisi duduk yang terang agar foto sesi terekam dengan jelas.',
      'Baca petunjuk ujian dan durasi yang diberikan sebelum memulai.',
    ]);

    ensureSpace(55);
    addSectionTitle('Langkah Pengerjaan');
    addBullets([
      'Login ke akun kandidat lalu buka menu ujian yang tersedia.',
      'Klik tombol mulai ujian dan ambil foto awal jika diminta oleh sistem.',
      'Kerjakan soal satu per satu sesuai instruksi pada layar.',
      'Perhatikan apakah soal berupa pilihan ganda atau essay.',
      'Jika pilihan ganda, pilih jawaban yang paling sesuai sebelum pindah ke soal berikutnya.',
      'Jika essay, ketik jawaban lengkap dan jelas pada kolom jawaban.',
    ]);

    ensureSpace(55);
    addSectionTitle('Aturan Saat Ujian');
    addBullets([
      'Jangan keluar dari halaman ujian tanpa alasan karena sistem dapat mencatat pelanggaran.',
      'Jangan membuka aplikasi atau tab lain jika aturan ujian melarangnya.',
      'Tetap berada di depan kamera agar foto dan aktivitas ujian bisa terpantau.',
      'Jika kamera atau koneksi terganggu, segera hubungi admin atau recruiter.',
    ]);

    ensureSpace(50);
    addSectionTitle('Setelah Selesai');
    addParagraph('Setelah semua soal selesai, klik submit atau selesai ujian. Sistem akan menyimpan jawaban, foto sesi, dan jika ada, catatan pelanggaran untuk bahan penilaian recruiter.');

    doc.save('panduan_pengerjaan_exam.pdf');
  };

  const downloadPsychotestProcedurePdf = () => {
    const doc = new jsPDF();
    const marginLeft = 14;
    const pageWidth = doc.internal.pageSize.getWidth();
    const contentWidth = pageWidth - (marginLeft * 2);
    let cursorY = 16;

    const addSectionTitle = (title) => {
      doc.setFontSize(14);
      doc.setFont('helvetica', 'bold');
      doc.text(title, marginLeft, cursorY);
      cursorY += 8;
    };

    const addParagraph = (text, size = 11) => {
      doc.setFontSize(size);
      doc.setFont('helvetica', 'normal');
      const lines = doc.splitTextToSize(text, contentWidth);
      doc.text(lines, marginLeft, cursorY);
      cursorY += lines.length * 5 + 2;
    };

    const addBullets = (items) => {
      doc.setFontSize(11);
      doc.setFont('helvetica', 'normal');
      items.forEach((item) => {
        const lines = doc.splitTextToSize(item, contentWidth - 6);
        doc.text(`• ${lines[0]}`, marginLeft, cursorY);
        if (lines.length > 1) {
          doc.text(lines.slice(1), marginLeft + 6, cursorY + 5);
          cursorY += lines.length * 5;
        } else {
          cursorY += 5;
        }
      });
      cursorY += 2;
    };

    const ensureSpace = (needed) => {
      if (cursorY + needed > 275) {
        doc.addPage();
        cursorY = 16;
      }
    };

    doc.setFontSize(18);
    doc.setFont('helvetica', 'bold');
    doc.text('Tata Cara Pengerjaan Psikotest', marginLeft, cursorY);
    cursorY += 8;
    addParagraph('Panduan ini menjelaskan cara mengikuti psikotest, dari pengecekan awal kamera sampai cara memilih jawaban A atau B.');

    ensureSpace(50);
    addSectionTitle('Sebelum Memulai');
    addBullets([
      'Pastikan kamera aktif karena sistem dapat mengambil foto awal sesi.',
      'Cari tempat yang tenang dan pencahayaan cukup.',
      'Siapkan fokus karena psikotest menilai kecenderungan jawaban, bukan jawaban benar atau salah.',
    ]);

    ensureSpace(55);
    addSectionTitle('Langkah Pengerjaan');
    addBullets([
      'Login ke akun kandidat lalu buka menu psikotest.',
      'Klik mulai psikotest dan izinkan sistem mengambil foto awal jika diminta.',
      'Baca dua pernyataan pada setiap soal dengan teliti.',
      'Pilih sisi A atau B yang paling sesuai dengan diri Anda.',
      'Kerjakan sampai semua soal selesai tanpa terlalu lama berpikir pada satu soal.',
    ]);

    ensureSpace(55);
    addSectionTitle('Hal Penting Saat Psikotest');
    addBullets([
      'Tidak ada jawaban benar atau salah, jadi pilih yang paling menggambarkan Anda.',
      'Jawab dengan konsisten agar hasil penilaian lebih akurat.',
      'Jangan menutup kamera atau berpindah halaman jika sistem melarangnya.',
      'Jika ada gangguan teknis, segera laporkan ke admin atau recruiter.',
    ]);

    ensureSpace(50);
    addSectionTitle('Setelah Selesai');
    addParagraph('Setelah jawaban terkirim, recruiter dapat melihat hasil, detail pertanyaan, jawaban, foto awal, dan ringkasan kecenderungan psikotest pada halaman detail kandidat.');

    doc.save('panduan_pengerjaan_psikotest.pdf');
  };

  const closeChangePasswordModal = () => {
    setShowChangePasswordModal(false);
    setPasswordForm({
      currentPassword: '',
      newPassword: '',
      confirmPassword: ''
    });
  };

  const handleChangePassword = async () => {
    const { currentPassword, newPassword, confirmPassword } = passwordForm;

    if (!currentPassword || !newPassword || !confirmPassword) {
      Swal.fire({ icon: 'warning', title: 'Perhatian', text: 'Semua field password wajib diisi' });
      return;
    }

    if (newPassword.length < 6) {
      Swal.fire({ icon: 'warning', title: 'Perhatian', text: 'Password baru minimal 6 karakter' });
      return;
    }

    if (newPassword !== confirmPassword) {
      Swal.fire({ icon: 'warning', title: 'Perhatian', text: 'Konfirmasi password baru tidak cocok' });
      return;
    }

    try {
      setChangingPassword(true);
      const res = await API.put('/auth/change-password', {
        currentPassword,
        newPassword
      });
      Swal.fire({ icon: 'success', title: 'Berhasil', text: res.data?.message || 'Password berhasil diubah', timer: 1800, showConfirmButton: false });
      closeChangePasswordModal();
    } catch (err) {
      Swal.fire({ icon: 'error', title: 'Error', text: err.response?.data?.message || err.message || 'Gagal mengubah password' });
    } finally {
      setChangingPassword(false);
    }
  };

  const handleLogout = async () => {
    const result = await Swal.fire({
      icon: 'question',
      title: 'Keluar akun?',
      text: 'Anda akan diarahkan ke halaman login.',
      showCancelButton: true,
      confirmButtonText: 'Ya, Logout',
      cancelButtonText: 'Batal',
    });

    if (!result.isConfirmed) return;

    setShowUserMenu(false);
    logout();
  };

  const openRecruiterForm = (rec = null) => {
    if (rec) {
      setEditingRecruiterId(rec.id);
      setRecruiterForm({
        name: rec.name || '',
        email: rec.email || '',
        password: ''
      });
    } else {
      setEditingRecruiterId(null);
      setRecruiterForm({ name: '', email: '', password: '' });
    }
    setShowRecruiterForm(true);
  };

  const saveRecruiter = async () => {
    if (!recruiterForm.name.trim() || !recruiterForm.email.trim()) {
      Swal.fire({ icon: 'warning', title: 'Perhatian', text: 'Nama dan email wajib diisi' });
      return;
    }
    if (!editingRecruiterId && !recruiterForm.password.trim()) {
      Swal.fire({ icon: 'warning', title: 'Perhatian', text: 'Password wajib diisi saat tambah recruiter' });
      return;
    }
    if (recruiterForm.password && recruiterForm.password.length < 6) {
      Swal.fire({ icon: 'warning', title: 'Perhatian', text: 'Password minimal 6 karakter' });
      return;
    }

    try {
      if (editingRecruiterId) {
        await API.put(`/admin/recruiters/${editingRecruiterId}`, recruiterForm);
        Swal.fire({ icon: 'success', title: 'Berhasil', text: 'Recruiter berhasil diperbarui', timer: 1500, showConfirmButton: false });
      } else {
        await API.post('/admin/recruiters', recruiterForm);
        Swal.fire({ icon: 'success', title: 'Berhasil', text: 'Recruiter berhasil ditambahkan', timer: 1500, showConfirmButton: false });
      }

      setShowRecruiterForm(false);
      setEditingRecruiterId(null);
      setRecruiterForm({ name: '', email: '', password: '' });
      const res = await API.get('/admin/recruiters');
      setRecruiters(res.data || []);
    } catch (err) {
      Swal.fire({ icon: 'error', title: 'Error', text: err.response?.data?.message || err.message });
    }
  };

  const removeRecruiter = async (recruiterId) => {
    const result = await Swal.fire({
      icon: 'warning',
      title: 'Hapus Recruiter?',
      text: 'Recruiter akan dihapus dari sistem.',
      showCancelButton: true,
      confirmButtonText: 'Ya, Hapus',
      cancelButtonText: 'Batal',
      confirmButtonColor: '#ef4444',
    });
    if (!result.isConfirmed) return;

    try {
      await API.delete(`/admin/recruiters/${recruiterId}`);
      Swal.fire({ icon: 'success', title: 'Dihapus', text: 'Recruiter berhasil dihapus', timer: 1500, showConfirmButton: false });
      const res = await API.get('/admin/recruiters');
      setRecruiters(res.data || []);
    } catch (err) {
      Swal.fire({ icon: 'error', title: 'Error', text: err.response?.data?.message || err.message });
    }
  };

  const editCandidate = (sess) => {
    setEditingCandidateId(sess.candidate_id);
    setCandidateForm({
      name: sess.candidate_name || '',
      email: sess.candidate_email || '',
      password: '',
      position: sess.position || 'general'
    });
    setShowCandidateForm(true);
  };

  const deleteCandidateRow = async (candidate_id) => {
    const result = await Swal.fire({
      icon: 'warning',
      title: 'Hapus Peserta?',
      text: 'Peserta akan dihapus dari sistem.',
      showCancelButton: true,
      confirmButtonText: 'Ya, Hapus',
      cancelButtonText: 'Batal',
      confirmButtonColor: '#ef4444',
    });
    if (!result.isConfirmed) return;

    try {
      await API.delete(`/recruiter/candidate/${candidate_id}`);
      Swal.fire({ icon: 'success', title: 'Dihapus', text: 'Peserta berhasil dihapus', timer: 1500, showConfirmButton: false });
      const res = await API.get('/recruiter/sessions');
      setSessions(res.data);
    } catch (err) {
      Swal.fire({ icon: 'error', title: 'Error', text: err.response?.data?.message || err.message });
    }
  };

  const saveCandidate = async () => {
    if (!candidateForm.name.trim() || !candidateForm.email.trim() || !candidateForm.position.trim()) {
      Swal.fire({ icon: 'warning', title: 'Perhatian', text: 'Nama, email, dan posisi harus diisi' });
      return;
    }

    try {
      if (editingCandidateId) {
        await API.put(`/recruiter/candidate/${editingCandidateId}`, { ...candidateForm });
        Swal.fire({ icon: 'success', title: 'Berhasil', text: 'Data peserta berhasil diperbarui', timer: 1500, showConfirmButton: false });
      } else {
        const res = await API.post('/recruiter/register-candidate', {
          name: candidateForm.name,
          email: candidateForm.email,
          password: candidateForm.password,
          position: candidateForm.position
        });
        Swal.fire({ icon: 'success', title: 'Berhasil', text: 'Peserta berhasil terdaftar', timer: 1500, showConfirmButton: false });
      }
      setShowCandidateForm(false);
      setEditingCandidateId(null);
      setCandidateForm({ name: '', email: '', password: '', position: 'general' });
      const res = await API.get('/recruiter/sessions');
      setSessions(res.data);
    } catch (err) {
      Swal.fire({ icon: 'error', title: 'Error', text: err.response?.data?.message || err.message });
    }
  };

  // ─── Exam CRUD ──────────────────────────────────────────────────────────────
  const saveExam = async () => {
    if (!examForm.title.trim()) {
      Swal.fire({ icon: 'warning', title: 'Perhatian', text: 'Judul ujian harus diisi' });
      return;
    }
    try {
      if (editingExam) {
        await API.put(`/recruiter/exam/${editingExam.id}`, examForm);
        Swal.fire({ icon: 'success', title: 'Berhasil', text: 'Ujian berhasil diperbarui', timer: 1500, showConfirmButton: false });
      } else {
        await API.post('/recruiter/exam', examForm);
        Swal.fire({ icon: 'success', title: 'Berhasil', text: 'Ujian berhasil dibuat', timer: 1500, showConfirmButton: false });
      }
      setShowExamForm(false);
      setEditingExam(null);
      setExamForm({ title: '', description: '', duration_minutes: 60, max_violations: 1, total: 0, multiple_choice: 1, passing_grade: 60, position: 'all' });
      // Reload exams
      const res = await API.get('/recruiter/exams');
      setExams(res.data);
    } catch (err) {
      Swal.fire({ icon: 'error', title: 'Error', text: err.response?.data?.message || err.message });
    }
  };

  const deleteExam = async (exam_id) => {
    const result = await Swal.fire({
      icon: 'warning',
      title: 'Hapus Ujian?',
      text: 'Ujian dan semua soalnya akan dihapus permanen.',
      showCancelButton: true,
      confirmButtonText: 'Ya, Hapus',
      cancelButtonText: 'Batal',
      confirmButtonColor: '#ef4444',
    });
    if (!result.isConfirmed) return;
    try {
      await API.delete(`/recruiter/exam/${exam_id}`);
      Swal.fire({ icon: 'success', title: 'Dihapus', text: 'Ujian berhasil dihapus', timer: 1500, showConfirmButton: false });
      const res = await API.get('/recruiter/exams');
      setExams(res.data);
    } catch (err) {
      Swal.fire({ icon: 'error', title: 'Error', text: err.response?.data?.message || err.message });
    }
  };

  // ─── Register Candidate ─────────────────────────────────────────────────────
  const registerCandidate = async () => {
    if (!candidateForm.name || !candidateForm.email || !candidateForm.password) {
      Swal.fire({ icon: 'warning', title: 'Perhatian', text: 'Semua field harus diisi' });
      return;
    }
    setRegistering(true);
    try {
      await API.post('/recruiter/register-candidate', {
        name: candidateForm.name,
        email: candidateForm.email,
        password: candidateForm.password,
        position: candidateForm.position
      });
      Swal.fire({ icon: 'success', title: 'Berhasil', text: 'Peserta berhasil terdaftar', timer: 1500, showConfirmButton: false });
      setCandidateForm({ name: '', email: '', password: '', position: 'general' });
      setShowCandidateForm(false);
      // Reload sessions
      const res = await API.get('/recruiter/sessions');
      setSessions(res.data);
    } catch (err) {
      Swal.fire({ icon: 'error', title: 'Error', text: err.response?.data?.message || err.message });
    } finally {
      setRegistering(false);
    }
  };

  const startEditExam = (exam) => {
    setEditingExam(exam);
    setExamForm({
      title: exam.title,
      description: exam.description,
      duration_minutes: exam.duration_minutes,
      max_violations: exam.max_violations,
      total: exam.total || 0,
      multiple_choice: exam.multiple_choice !== undefined ? exam.multiple_choice : 1,
      passing_grade: exam.passing_grade || 60,
      position: exam.position || 'all'
    });
    setShowExamForm(true);
  };

  // ─── Psychotest CRUD ───────────────────────────────────────────────────────
  const resetPsychotestForm = () => {
    setEditingPsychotestQuestion(null);
    setPsychotestForm({
      dimension_a: 'extro_intro',
      statement_a: '',
      dimension_b: 'emotional_stability',
      statement_b: '',
      is_active: 1,
    });
  };

  const reloadPsychotestQuestions = async () => {
    const res = await API.get('/recruiter/psychotest/questions');
    setPsychotestQuestions(res.data);
    setPsychotestPage(1);
    setSelectedPsychotestQuestionIds([]);
    setSelectAllPsychotestQuestions(false);
  };

  const savePsychotestQuestion = async () => {
    if (!psychotestForm.statement_a.trim() || !psychotestForm.statement_b.trim()) {
      Swal.fire({ icon: 'warning', title: 'Perhatian', text: 'Pernyataan A dan B harus diisi' });
      return;
    }
    if (psychotestForm.dimension_a === psychotestForm.dimension_b) {
      Swal.fire({ icon: 'warning', title: 'Perhatian', text: 'Dimensi A dan B harus berbeda' });
      return;
    }

    try {
      if (editingPsychotestQuestion) {
        await API.put(`/recruiter/psychotest/question/${editingPsychotestQuestion.id}`, psychotestForm);
        Swal.fire({ icon: 'success', title: 'Berhasil', text: 'Soal psikotest diperbarui', timer: 1500, showConfirmButton: false });
      } else {
        await API.post('/recruiter/psychotest/question', psychotestForm);
        Swal.fire({ icon: 'success', title: 'Berhasil', text: 'Soal psikotest ditambahkan', timer: 1500, showConfirmButton: false });
      }
      setShowPsychotestForm(false);
      resetPsychotestForm();
      await reloadPsychotestQuestions();
    } catch (err) {
      Swal.fire({ icon: 'error', title: 'Error', text: err.response?.data?.message || err.message });
    }
  };

  const startEditPsychotestQuestion = (row) => {
    setEditingPsychotestQuestion(row);
    setPsychotestForm({
      dimension_a: row.dimension_a,
      statement_a: row.statement_a,
      dimension_b: row.dimension_b,
      statement_b: row.statement_b,
      is_active: Number(row.is_active) === 1 ? 1 : 0,
    });
    setShowPsychotestForm(true);
  };

  const deletePsychotestQuestion = async (question_id) => {
    const result = await Swal.fire({
      icon: 'warning',
      title: 'Hapus Soal Psikotest?',
      text: 'Jika soal sudah dipakai, sistem akan menonaktifkan soal (soft delete).',
      showCancelButton: true,
      confirmButtonText: 'Ya, Hapus',
      cancelButtonText: 'Batal',
      confirmButtonColor: '#ef4444',
    });
    if (!result.isConfirmed) return;
    try {
      await API.delete(`/recruiter/psychotest/question/${question_id}`);
      Swal.fire({ icon: 'success', title: 'Berhasil', text: 'Soal diproses', timer: 1500, showConfirmButton: false });
      await reloadPsychotestQuestions();
    } catch (err) {
      Swal.fire({ icon: 'error', title: 'Error', text: err.response?.data?.message || err.message });
    }
  };

  const uploadPsychotestExcelFile = async () => {
    if (!psychotestExcelFile) {
      Swal.fire({ icon: 'warning', title: 'Perhatian', text: 'Pilih file Excel terlebih dahulu' });
      return;
    }
    try {
      const formData = new FormData();
      formData.append('file', psychotestExcelFile);
      const res = await API.post('/recruiter/psychotest/questions/upload-excel', formData, {
        headers: { 'Content-Type': 'multipart/form-data' }
      });
      Swal.fire({ icon: 'success', title: 'Berhasil', text: res.data.message, timer: 2000, showConfirmButton: false });
      setPsychotestExcelFile(null);
      await reloadPsychotestQuestions();
    } catch (err) {
      Swal.fire({ icon: 'error', title: 'Error', text: err.response?.data?.message || err.message });
    }
  };

  const bulkDeleteAllPsychotestQuestions = async () => {
    const result = await Swal.fire({
      icon: 'warning',
      title: 'Hapus Semua Soal Psikotest?',
      text: 'Semua bank soal psikotest akan diproses. Soal yang sudah pernah dipakai akan dinonaktifkan, sisanya dihapus permanen.',
      showCancelButton: true,
      confirmButtonText: 'Ya, Hapus Semua',
      cancelButtonText: 'Batal',
      confirmButtonColor: '#ef4444',
    });
    if (!result.isConfirmed) return;

    try {
      const res = await API.post('/recruiter/psychotest/questions/bulk-delete');
      Swal.fire({ icon: 'success', title: 'Berhasil', text: res.data?.message || 'Soal psikotest diproses', timer: 2200, showConfirmButton: false });
      await reloadPsychotestQuestions();
    } catch (err) {
      Swal.fire({ icon: 'error', title: 'Error', text: err.response?.data?.message || err.message });
    }
  };

  const togglePsychotestQuestionSelection = (question_id, checked) => {
    setSelectedPsychotestQuestionIds((prev) => {
      const next = checked
        ? [...new Set([...prev, question_id])]
        : prev.filter((id) => id !== question_id);

      setSelectAllPsychotestQuestions(
        psychotestQuestions.length > 0 && next.length === psychotestQuestions.length
      );
      return next;
    });
  };

  const toggleSelectAllPsychotestQuestions = () => {
    setSelectAllPsychotestQuestions((prev) => {
      const next = !prev;
      if (next) {
        setSelectedPsychotestQuestionIds(psychotestQuestions.map((q) => q.id));
      } else {
        setSelectedPsychotestQuestionIds([]);
      }
      return next;
    });
  };

  const bulkDeleteSelectedPsychotestQuestions = async () => {
    if (selectedPsychotestQuestionIds.length === 0) {
      Swal.fire({ icon: 'warning', title: 'Perhatian', text: 'Pilih minimal satu soal psikotest' });
      return;
    }

    const result = await Swal.fire({
      icon: 'warning',
      title: `Hapus ${selectedPsychotestQuestionIds.length} Soal Psikotest?`,
      text: 'Soal yang sudah pernah dipakai akan dinonaktifkan, sisanya dihapus permanen.',
      showCancelButton: true,
      confirmButtonText: 'Ya, Hapus Terpilih',
      cancelButtonText: 'Batal',
      confirmButtonColor: '#ef4444',
    });
    if (!result.isConfirmed) return;

    try {
      const deleteResults = await Promise.allSettled(
        selectedPsychotestQuestionIds.map((id) => API.delete(`/recruiter/psychotest/question/${id}`))
      );

      const successCount = deleteResults.filter((r) => r.status === 'fulfilled').length;
      const failedCount = deleteResults.length - successCount;

      if (failedCount === 0) {
        Swal.fire({
          icon: 'success',
          title: 'Berhasil',
          text: `${successCount} soal psikotest berhasil diproses`,
          timer: 2200,
          showConfirmButton: false,
        });
      } else {
        Swal.fire({
          icon: 'warning',
          title: 'Selesai Sebagian',
          text: `${successCount} berhasil, ${failedCount} gagal. Silakan coba lagi untuk yang gagal.`,
        });
      }

      await reloadPsychotestQuestions();
    } catch (err) {
      Swal.fire({ icon: 'error', title: 'Error', text: err.response?.data?.message || err.message });
    }
  };

  const savePsychotestDuration = async () => {
    if (!Number.isFinite(Number(psychotestDurationMinutes)) || Number(psychotestDurationMinutes) < 1 || Number(psychotestDurationMinutes) > 180) {
      Swal.fire({ icon: 'warning', title: 'Perhatian', text: 'Durasi psikotest harus antara 1 sampai 180 menit' });
      return;
    }

    if (!Number.isFinite(Number(psychotestTotalQuestions)) || Number(psychotestTotalQuestions) < 0 || Number(psychotestTotalQuestions) > 500) {
      Swal.fire({ icon: 'warning', title: 'Perhatian', text: 'Jumlah soal tampil harus antara 0 sampai 500 (0 = semua soal)' });
      return;
    }

    if (!Number.isFinite(Number(psychotestMaxViolations)) || Number(psychotestMaxViolations) < 1 || Number(psychotestMaxViolations) > 20) {
      Swal.fire({ icon: 'warning', title: 'Perhatian', text: 'Batas maksimal pelanggaran harus antara 1 sampai 20' });
      return;
    }

    setSavingPsychotestDuration(true);
    try {
      await API.put('/recruiter/psychotest/settings', {
        duration_minutes: Number(psychotestDurationMinutes),
        total_questions: Number(psychotestTotalQuestions),
        max_violations: Number(psychotestMaxViolations),
      });
      Swal.fire({ icon: 'success', title: 'Berhasil', text: 'Pengaturan psikotest diperbarui', timer: 1500, showConfirmButton: false });
    } catch (err) {
      Swal.fire({ icon: 'error', title: 'Error', text: err.response?.data?.message || err.message });
    } finally {
      setSavingPsychotestDuration(false);
    }
  };

  // ─── Toggle exam expansion and load questions ───────────────────────────────
  const toggleExamExpanded = async (exam_id) => {
    if (expandedExams[exam_id]) {
      setExpandedExams({...expandedExams, [exam_id]: false});
      return;
    }
    setExpandedExams({...expandedExams, [exam_id]: true});
    if (selectedExamForQuestions !== exam_id) {
      setSelectedExamForQuestions(exam_id);
    }
  };

  // ─── Question CRUD ──────────────────────────────────────────────────────────
  const uploadQuestionImageFile = async (question_id) => {
    if (!questionPhotoFile) return;
    try {
      const formData = new FormData();
      formData.append('question_id', question_id);
      formData.append('photo', questionPhotoFile);
      await API.post('/recruiter/question/upload-image', formData, {
        headers: { 'Content-Type': 'multipart/form-data' }
      });
    } catch (err) {
      console.error('Upload question image error', err);
    }
  };

  const validateQuestionOptions = () => {
    if (!questionForm.question_text.trim()) {
      Swal.fire({ icon: 'warning', title: 'Perhatian', text: 'Pertanyaan harus diisi' });
      return false;
    }
    // Find current exam to check type
    const currentExam = exams.find(e => e.id === selectedExamForQuestions);
    const isMultipleChoice = !currentExam || currentExam.multiple_choice === 1;
    if (isMultipleChoice) {
      const options = ['option_a', 'option_b', 'option_c', 'option_d', 'option_e'];
      const filled = options.filter(opt => questionForm[opt]?.trim());
      if (filled.length < 2) {
        Swal.fire({ icon: 'warning', title: 'Perhatian', text: 'Minimal harus ada 2 pilihan jawaban yang diisi' });
        return false;
      }
      const correctKey = `option_${questionForm.correct_answer}`;
      if (!questionForm.correct_answer || !questionForm[correctKey]?.trim()) {
        Swal.fire({ icon: 'warning', title: 'Perhatian', text: 'Jawaban benar harus dipilih dari pilihan yang tersedia' });
        return false;
      }
    }
    return true;
  };

  const saveQuestion = async () => {
    if (!validateQuestionOptions()) return;

    try {
      if (editingQuestion) {
        await API.put(`/recruiter/question/${editingQuestion.id}`, questionForm);
        if (questionPhotoFile) await uploadQuestionImageFile(editingQuestion.id);
        Swal.fire({ icon: 'success', title: 'Berhasil', text: 'Soal berhasil diperbarui', timer: 1500, showConfirmButton: false });
      } else {
        const res = await API.post('/recruiter/question', {
          exam_id: selectedExamForQuestions,
          ...questionForm
        });
        if (res.data.question_id && questionPhotoFile) {
          await uploadQuestionImageFile(res.data.question_id);
        }
        Swal.fire({ icon: 'success', title: 'Berhasil', text: 'Soal berhasil ditambahkan', timer: 1500, showConfirmButton: false });
      }
      setShowQuestionForm(false);
      setEditingQuestion(null);
      setQuestionPhotoFile(null);
      setQuestionPhotoPreview(null);
      setQuestionForm({
        question_text: '',
        option_a: '',
        option_b: '',
        option_c: '',
        option_d: '',
        option_e: '',
        correct_answer: 'a',
        points: 10,
        photo: null
      });
      // Reload questions
      const reload = await API.get(`/recruiter/exam/${selectedExamForQuestions}/questions`);
      setQuestions(reload.data);
    } catch (err) {
      Swal.fire({ icon: 'error', title: 'Error', text: err.response?.data?.message || err.message });
    }
  };

  const deleteQuestion = async (question_id) => {
    const result = await Swal.fire({
      icon: 'warning',
      title: 'Hapus Soal?',
      text: 'Soal ini akan dihapus permanen.',
      showCancelButton: true,
      confirmButtonText: 'Ya, Hapus',
      cancelButtonText: 'Batal',
      confirmButtonColor: '#ef4444',
    });
    if (!result.isConfirmed) return;
    try {
      await API.delete(`/recruiter/question/${question_id}`);
      Swal.fire({ icon: 'success', title: 'Dihapus', text: 'Soal berhasil dihapus', timer: 1500, showConfirmButton: false });
      const res = await API.get(`/recruiter/exam/${selectedExamForQuestions}/questions`);
      setQuestions(res.data);
    } catch (err) {
      Swal.fire({ icon: 'error', title: 'Error', text: err.response?.data?.message || err.message });
    }
  };

  const toggleQuestionSelection = (question_id, checked) => {
    setSelectedQuestionIds(prev => {
      if (checked) return [...new Set([...prev, question_id])];
      return prev.filter(id => id !== question_id);
    });
  };

  const toggleSelectAllQuestions = () => {
    setSelectAllQuestions(prev => {
      const next = !prev;
      if (next) {
        setSelectedQuestionIds(questions.map(q => q.id));
      } else {
        setSelectedQuestionIds([]);
      }
      return next;
    });
  };

  const bulkDeleteSelectedQuestions = async () => {
    if (selectedQuestionIds.length === 0) {
      Swal.fire({ icon: 'warning', title: 'Perhatian', text: 'Pilih minimal satu soal untuk dihapus' });
      return;
    }
    const result = await Swal.fire({
      icon: 'warning',
      title: `Hapus ${selectedQuestionIds.length} Soal?`,
      text: 'Soal yang dipilih akan dihapus permanen.',
      showCancelButton: true,
      confirmButtonText: 'Ya, Hapus',
      cancelButtonText: 'Batal',
      confirmButtonColor: '#ef4444',
    });
    if (!result.isConfirmed) return;
    try {
      await API.post('/recruiter/questions/bulk-delete', { question_ids: selectedQuestionIds });
      Swal.fire({ icon: 'success', title: 'Dihapus', text: 'Soal terpilih berhasil dihapus', timer: 1500, showConfirmButton: false });
      const res = await API.get(`/recruiter/exam/${selectedExamForQuestions}/questions`);
      setQuestions(res.data);
      setSelectedQuestionIds([]);
      setSelectAllQuestions(false);
    } catch (err) {
      Swal.fire({ icon: 'error', title: 'Error', text: err.response?.data?.message || err.message });
    }
  };

  const uploadQuestionsExcelFile = async () => {
    if (!excelFile) {
      Swal.fire({ icon: 'warning', title: 'Perhatian', text: 'Pilih file Excel terlebih dahulu' });
      return;
    }
    if (!selectedExamForQuestions) {
      Swal.fire({ icon: 'warning', title: 'Perhatian', text: 'Pilih ujian terlebih dahulu' });
      return;
    }
    try {
      const formData = new FormData();
      formData.append('file', excelFile);
      formData.append('exam_id', selectedExamForQuestions);
      const res = await API.post('/recruiter/questions/upload-excel', formData, {
        headers: { 'Content-Type': 'multipart/form-data' }
      });
      Swal.fire({ icon: 'success', title: 'Berhasil', text: res.data.message, timer: 2000, showConfirmButton: false });
      setExcelFile(null);
      const qres = await API.get(`/recruiter/exam/${selectedExamForQuestions}/questions`);
      setQuestions(qres.data);
    } catch (err) {
      Swal.fire({ icon: 'error', title: 'Error', text: err.response?.data?.message || err.message });
    }
  };

  const startEditQuestion = (q) => {
    setEditingQuestion(q);
    setQuestionPhotoFile(null);
    setQuestionPhotoPreview(q.photo ? `/uploads/questions/${q.photo}` : null);
    setQuestionForm({
      question_text: q.question_text,
      option_a: q.option_a,
      option_b: q.option_b,
      option_c: q.option_c,
      option_d: q.option_d,
      option_e: q.option_e || '',
      correct_answer: q.correct_answer,
      points: q.points,
      photo: q.photo || null
    });
    setShowQuestionForm(true);
  };

  // ─── Filters for sessions ───────────────────────────────────────────────────
  const filtered = sessions.filter(s => {
    const search = candidateSearch.trim().toLowerCase();
    if (search) {
      const text = `${s.candidate_name || ''} ${s.candidate_email || ''} ${s.position || ''}`.toLowerCase();
      if (!text.includes(search)) return false;
    }
    if (filter === 'all') return true;
    if (filter === 'passed') return (s.overall_status || 'in_progress') === 'lulus';
    if (filter === 'failed') return (s.overall_status || 'in_progress') === 'tidak_lulus';
    if (filter === 'active') return (s.overall_status || 'in_progress') === 'in_progress';
    if (filter === 'disqualified') return s.is_disqualified;
    return true;
  });

  const effectiveCandidatePerPage = resolvePageSize(candidatePerPage, filtered.length);
  const totalCandidatePages = Math.max(1, Math.ceil(filtered.length / effectiveCandidatePerPage));
  const paginatedCandidates = filtered.slice(
    (candidatePage - 1) * effectiveCandidatePerPage,
    candidatePage * effectiveCandidatePerPage
  );

  const candidateDetailExams = candidateDetail?.exams || [];
  const candidatePsychotestSummary = candidateDetail ? formatPsychotestResult(candidateDetail.candidate?.psychotest_result) : null;
  const effectiveDetailExamPerPage = resolvePageSize(detailExamPerPage, candidateDetailExams.length);
  const totalDetailExamPages = Math.max(1, Math.ceil(candidateDetailExams.length / effectiveDetailExamPerPage));
  const paginatedCandidateDetailExams = candidateDetailExams.slice(
    (detailExamPage - 1) * effectiveDetailExamPerPage,
    detailExamPage * effectiveDetailExamPerPage
  );

  const stats = {
    total: sessions.length,
    active: sessions.filter(s => (s.overall_status || 'in_progress') === 'in_progress').length,
    finished: sessions.filter(s => (s.overall_status || 'in_progress') !== 'in_progress').length,
    disq: sessions.filter(s => s.is_disqualified).length,
  };

  const effectivePsychotestPerPage = resolvePageSize(psychotestPerPage, psychotestQuestions.length);
  const totalPsychotestPages = Math.max(1, Math.ceil(psychotestQuestions.length / effectivePsychotestPerPage));
  const paginatedPsychotestQuestions = psychotestQuestions.slice(
    (psychotestPage - 1) * effectivePsychotestPerPage,
    psychotestPage * effectivePsychotestPerPage
  );

  return (
    <div className="rec-page">
      <header className="rec-header">
        <div className="rec-logo">📋 Rekrutmen Dashboard</div>
        <div className="rec-user" ref={userMenuRef}>
          <button
            type="button"
            className="user-menu-trigger"
            onClick={() => setShowUserMenu((prev) => !prev)}
          >
            👤 {user?.name}
            <span className="user-menu-chevron">▾</span>
          </button>
          {showUserMenu && (
            <div className="user-menu-dropdown">
              <button
                type="button"
                className="user-menu-item"
                onClick={downloadDashboardTutorialPdf}
              >
                📘 Tutorial Sistem
              </button>
              <button
                type="button"
                className="user-menu-item"
                onClick={downloadExamProcedurePdf}
              >
                📝 Tata Cara Exam
              </button>
              <button
                type="button"
                className="user-menu-item"
                onClick={downloadPsychotestProcedurePdf}
              >
                🧠 Tata Cara Psikotest
              </button>
              <button
                type="button"
                className="user-menu-item"
                onClick={() => {/* Lines 1343-1345 omitted */}}
              >
                🔐 Change Password
              </button>
              <button
                type="button"
                className="user-menu-item danger"
                onClick={handleLogout}
              >
                🚪 Logout
              </button>
            </div>
          )}
        </div>
      </header>

      {showChangePasswordModal && (
        <div className="form-modal" onClick={closeChangePasswordModal}>
          <div className="form-box change-password-box" onClick={(e) => e.stopPropagation()}>
            <div className="modal-header">
              <h3>Change Password</h3>
              <button className="modal-close-btn" onClick={closeChangePasswordModal}>✕</button>
            </div>
            <div className="form-group">
              <label>Password Saat Ini</label>
              <input
                type="password"
                value={passwordForm.currentPassword}
                onChange={(e) => setPasswordForm({ ...passwordForm, currentPassword: e.target.value })}
                placeholder="Masukkan password saat ini"
              />
            </div>
            <div className="form-group">
              <label>Password Baru</label>
              <input
                type="password"
                value={passwordForm.newPassword}
                onChange={(e) => setPasswordForm({ ...passwordForm, newPassword: e.target.value })}
                placeholder="Minimal 6 karakter"
              />
            </div>
            <div className="form-group">
              <label>Konfirmasi Password Baru</label>
              <input
                type="password"
                value={passwordForm.confirmPassword}
                onChange={(e) => setPasswordForm({ ...passwordForm, confirmPassword: e.target.value })}
                placeholder="Ulangi password baru"
              />
            </div>
            <div className="form-actions">
              <button className="btn-save" onClick={handleChangePassword} disabled={changingPassword}>
                {changingPassword ? 'Menyimpan...' : 'Simpan Password'}
              </button>
              <button className="btn-cancel" onClick={closeChangePasswordModal}>Batal</button>
            </div>
          </div>
        </div>
      )}

      <div className="rec-body">
        {/* Tab Navigation */}
        <div className="tab-nav">
          <button
            className={`tab-btn ${activeTab === 'candidates' ? 'active' : ''}`}
            onClick={() => setActiveTab('candidates')}
          >
            👥 Peserta Ujian
          </button>
          <button
            className={`tab-btn ${activeTab === 'exams' ? 'active' : ''}`}
            onClick={() => setActiveTab('exams')}
          >
            📝 Kelola Ujian & Soal
          </button>
          <button
            className={`tab-btn ${activeTab === 'psychotest' ? 'active' : ''}`}
            onClick={() => setActiveTab('psychotest')}
          >
            🧠 Kelola Psikotest
          </button>
          {user?.role === 'admin' && (
            <button
              className={`tab-btn ${activeTab === 'recruiters' ? 'active' : ''}`}
              onClick={() => setActiveTab('recruiters')}
            >
              🧑‍💼 Kelola Recruiter
            </button>
          )}
        </div>

        {/* ─── CANDIDATES TAB ─────────────────────────────────────────────────*/}
        {activeTab === 'candidates' && (
          <>
            {/* Stats */}
            <div className="rec-stats">
              <div className="stat-card">
                <div className="sc-val">{stats.total}</div>
                <div className="sc-label">Total Peserta</div>
              </div>
              <div className="stat-card active">
                <div className="sc-val">{stats.active}</div>
                <div className="sc-label">Sedang Ujian</div>
              </div>
              <div className="stat-card finished">
                <div className="sc-val">{stats.finished}</div>
                <div className="sc-label">Selesai</div>
              </div>
              <div className="stat-card disq">
                <div className="sc-val">{stats.disq}</div>
                <div className="sc-label">Diskualifikasi</div>
              </div>
            </div>

            <div className="rec-content">
              {/* Left: sessions table */}
              <div className="sessions-panel">
                <div className="panel-header">
                  <h2>Data Peserta</h2>
                  <button className="btn-primary" onClick={() => setShowCandidateForm(!showCandidateForm)}>
                    {showCandidateForm ? '✕ Tutup Form' : '➕ Daftar Peserta Baru'}
                  </button>
                </div>

                <div className="table-toolbar">
                  <input
                    className="search-input"
                    type="text"
                    placeholder="Cari peserta..."
                    value={candidateSearch}
                    onChange={(e) => setCandidateSearch(e.target.value)}
                  />
                </div>

                {showCandidateForm && (
                  <div className="form-modal">
                    <div className="form-box">
                      <div className="modal-header">
                        <h3>Daftar Peserta Baru</h3>
                        <button className="modal-close-btn" onClick={() => setShowCandidateForm(false)}>✕</button>
                      </div>
                      <div className="form-group">
                        <label>Nama Lengkap</label>
                        <input
                          type="text"
                          value={candidateForm.name}
                          onChange={(e) => setCandidateForm({...candidateForm, name: e.target.value})}
                          placeholder="Nama peserta"
                        />
                      </div>
                      <div className="form-group">
                        <label>Email</label>
                        <input
                          type="email"
                          value={candidateForm.email}
                          onChange={(e) => setCandidateForm({...candidateForm, email: e.target.value})}
                          placeholder="email@example.com"
                        />
                      </div>
                      <div className="form-group">
                        <label>Password</label>
                        <input
                          type="password"
                          value={candidateForm.password}
                          onChange={(e) => setCandidateForm({...candidateForm, password: e.target.value})}
                          placeholder="Min. 6 karakter"
                        />
                      </div>
                      <div className="form-group">
                        <label>Posisi</label>
                        <select
                          value={candidateForm.position}
                          onChange={(e) => setCandidateForm({...candidateForm, position: e.target.value})}
                        >
                          <option value="software">Software Developer</option>
                          <option value="hardware">Hardware Engineer</option>
                          <option value="general">General</option>
                        </select>
                      </div>
                      <div className="form-actions">
                        <button className="btn-primary" onClick={registerCandidate} disabled={registering}>
                          {registering ? 'Memproses...' : 'Daftar'}
                        </button>
                        <button className="btn-secondary" onClick={() => setShowCandidateForm(false)}>
                          Batal
                        </button>
                      </div>
                    </div>
                  </div>
                )}

                <div className="filter-tabs">
                  {['all','active','passed','failed','disqualified'].map(f => (
                    <button key={f} className={`ftab ${filter===f?'active':''}`} onClick={() => setFilter(f)}>
                      {f === 'all' ? 'Semua Candidate' : f === 'active' ? 'Aktif' : f === 'passed' ? 'Lulus' : f === 'failed' ? 'Tidak Lulus' : 'Diskualifikasi'}
                    </button>
                  ))}
                </div>

                <div className="bulk-actions" style={{ marginBottom: '12px' }}>
                  <div className="bulk-actions-left">
                    <label className="upload-label">
                      Show List:
                      <select value={candidatePerPage} onChange={(e) => setCandidatePerPage(e.target.value)}>
                        <option value="10">10</option>
                        <option value="25">25</option>
                        <option value="50">50</option>
                        <option value="all">All</option>
                      </select>
                    </label>
                    <button type="button" className="btn-info" onClick={exportCandidatesExcel}>
                      ⬇️ Export Excel
                    </button>
                  </div>
                </div>

                {loadingSessions ? <div className="loading-text">Memuat data...</div> : (
                  <div className="sessions-table">
                    <table>
                      <thead>
                        <tr>
                          <th>No</th>
                          <th>Foto</th>
                          <th>Nama</th>
                          <th>Email</th>
                          <th>Posisi</th>
                          <th>Status</th>
                          <th>Nilai</th>
                          <th>Pelanggaran</th>
                          <th>Aksi</th>
                        </tr>
                      </thead>
                      <tbody>
                        {paginatedCandidates.map((s, idx) => {
                          const overallStatus = s.overall_status || 'in_progress';
                          const scoreValue = Number(s.score || 0);
                          const badgeClass = overallStatus === 'lulus'
                            ? 'pass'
                            : overallStatus === 'tidak_lulus'
                              ? 'fail'
                              : 'active';

                          return (
                          <tr key={`${s.candidate_id}-${s.id || 'none'}`} className={selected?.id === s.id ? 'row-selected' : ''}>
                            <td>{((candidatePage - 1) * effectiveCandidatePerPage) + idx + 1}</td>
                            <td>
                              {s.photo ? (
                                <img
                                  src={buildUploadUrl('face', s.photo)}
                                  alt={`Foto sesi ${s.candidate_name}`}
                                  style={{ width: '64px', height: '48px', objectFit: 'cover', borderRadius: '6px', border: '1px solid var(--border)' }}
                                />
                              ) : (
                                '-'
                              )}
                            </td>
                            <td>{s.candidate_name}</td>
                            <td>{s.candidate_email}</td>
                            <td>{s.position || '-'}</td>
                            <td>
                              <span className={`badge ${badgeClass}`}>
                                {overallStatus === 'lulus'
                                  ? '✅ Lulus'
                                  : overallStatus === 'tidak_lulus'
                                    ? '❌ Tidak Lulus'
                                    : '🟡 In Progress'}
                              </span>
                            </td>
                            <td>{overallStatus === 'in_progress' ? '-' : `${scoreValue}%`}</td>
                            <td className={(s.violation_count || 0) > 0 ? 'text-red' : ''}>{s.violation_count || 0}</td>
                            <td className="td-actions-row">
                              <button className="btn-primary" onClick={() => openCandidateDetail(s)}>
                                Detail
                              </button>
                              
                              <button className="btn-secondary" onClick={() => editCandidate(s)}>
                                Edit
                              </button>
                              <button className="btn-danger" onClick={() => deleteCandidateRow(s.candidate_id)}>
                                Hapus
                              </button>
                              <button
                                className="btn-info"
                                onClick={() => sendCandidateResultEmail(s)}
                                disabled={overallStatus === 'in_progress'}
                              >
                                Kirim Email
                              </button>
                            </td>
                          </tr>
                          );
                        })}
                        {paginatedCandidates.length === 0 && (
                          <tr><td colSpan="9" className="empty-row">Tidak ada data</td></tr>
                        )}
                      </tbody>
                    </table>

                    {totalCandidatePages > 1 && (
                      <div className="pagination">
                        <button
                          className="pagination-btn"
                          onClick={() => setCandidatePage(Math.max(1, candidatePage - 1))}
                          disabled={candidatePage === 1}
                        >
                          ← Sebelumnya
                        </button>
                        <div className="pagination-info">
                          Halaman {candidatePage} dari {totalCandidatePages} (Total: {filtered.length} peserta)
                        </div>
                        <button
                          className="pagination-btn"
                          onClick={() => setCandidatePage(Math.min(totalCandidatePages, candidatePage + 1))}
                          disabled={candidatePage === totalCandidatePages}
                        >
                          Berikutnya →
                        </button>
                      </div>
                    )}
                  </div>
                )}
              </div>

              {showCandidateDetailModal && (
  <div className="form-modal" onClick={() => setShowCandidateDetailModal(false)}>
    <div className="form-box large modal-box" onClick={(e) => e.stopPropagation()}>
      <div className="modal-header">
        <h3>Detail Kandidat</h3>
        <button className="modal-close-btn" onClick={() => setShowCandidateDetailModal(false)}>✕</button>
      </div>

      {candidateDetailLoading ? (
        <div className="loading-text">Memuat detail kandidat...</div>
      ) : candidateDetail ? (
        <div className="modal-body">

          {/* === Info Kandidat === */}
          <div className="candidate-info-card">
            <div className="detail-grid">
              <div>
                <p className="detail-label">Nama</p>
                <p className="detail-value">{candidateDetail.candidate.name}</p>
              </div>
              <div>
                <p className="detail-label">Email</p>
                <p className="detail-value">{candidateDetail.candidate.email}</p>
              </div>
              <div>
                <p className="detail-label">Posisi</p>
                <p className="detail-value">{candidateDetail.candidate.position || '-'}</p>
              </div>
              
            </div>
          </div>

          {/* === Action Buttons === */}
          <div className="bulk-actions candidate-actions">
            <div className="bulk-actions-left">
              <button type="button" className="btn-primary" onClick={downloadCandidateDetailPdf}>
                📄 Download PDF Detail
              </button>
              <button type="button" className="btn-danger" onClick={resetCandidateExamData}>
                ♻ Reset Exam
              </button>
              <button type="button" className="btn-danger" onClick={resetCandidatePsychotestData}>
                ♻ Reset Psikotest
              </button>
            </div>
          </div>

          {/* === Riwayat Ujian === */}
          <div className="section-block">
            <div className="section-header-row">
              <h4>Riwayat Ujian ({candidateDetail.exams.length})</h4>
            </div>

            {candidateDetail.exams.length === 0 ? (
              <div className="empty-state">Belum ada riwayat ujian</div>
            ) : (
              <>
                <div className="bulk-actions" style={{ marginBottom: '10px' }}>
                  <div className="bulk-actions-left">
                    <label className="upload-label">
                      Show List:
                      <select value={detailExamPerPage} onChange={(e) => setDetailExamPerPage(e.target.value)}>
                        <option value="10">10</option>
                        <option value="25">25</option>
                        <option value="50">50</option>
                        <option value="all">All</option>
                      </select>
                    </label>
                    <button type="button" className="btn-info" onClick={exportCandidateExamHistoryExcel}>
                      ⬇️ Export Excel
                    </button>
                  </div>
                </div>

                <div className="sessions-table">
                  <table>
                    <thead>
                      <tr>
                        <th>No</th>
                        <th>Ujian</th>
                        <th>Foto</th>
                        <th>Status</th>
                        <th>Nilai</th>
                        <th>Pelanggaran</th>
                        <th>Waktu</th>
                        <th>Aksi</th>
                      </tr>
                    </thead>
                    <tbody>
                      {paginatedCandidateDetailExams.map((exam, idx) => {
                        const statusLabel = exam.is_disqualified
                          ? 'Diskualifikasi'
                          : exam.status === 'active'
                          ? 'Sedang Ujian'
                          : exam.status === 'finished'
                          ? 'Selesai'
                          : exam.status;

                        const statusClass = exam.is_disqualified
                          ? 'status-danger'
                          : exam.status === 'active'
                          ? 'status-warning'
                          : exam.status === 'finished'
                          ? 'status-done'
                          : 'status-neutral';

                        return (
                          <tr key={exam.session_id}>
                            <td>{((detailExamPage - 1) * effectiveDetailExamPerPage) + idx + 1}</td>
                            <td>{exam.exam_title}</td>
                            <td>
                              {exam.session_photo ? (
                                <img
                                  src={buildUploadUrl('face', exam.session_photo)}
                                  alt={`Foto sesi ${exam.exam_title}`}
                                  className="thumb-photo"
                                />
                              ) : (
                                '-'
                              )}
                            </td>
                            <td>
                              <span className={`status-pill ${statusClass}`}>{statusLabel}</span>
                            </td>
                            <td>{exam.score != null ? `${exam.score}%` : '-'}</td>
                            <td>{exam.violation_count || 0}</td>
                            <td>{exam.started_at ? new Date(exam.started_at).toLocaleString('id-ID') : '-'}</td>
                            <td>
                              <button
                                type="button"
                                className="btn-secondary"
                                onClick={() => openSessionAnswerDetail(exam)}
                                disabled={!exam.session_id}
                              >
                                Detail Jawaban
                              </button>
                            </td>
                          </tr>
                        );
                      })}
                    </tbody>
                  </table>

                  {totalDetailExamPages > 1 && (
                    <div className="pagination" style={{ marginTop: '10px' }}>
                      <button
                        className="pagination-btn"
                        onClick={() => setDetailExamPage(Math.max(1, detailExamPage - 1))}
                        disabled={detailExamPage === 1}
                      >
                        ← Sebelumnya
                      </button>
                      <div className="pagination-info">
                        Halaman {detailExamPage} dari {totalDetailExamPages} (Total: {candidateDetailExams.length} riwayat)
                      </div>
                      <button
                        className="pagination-btn"
                        onClick={() => setDetailExamPage(Math.min(totalDetailExamPages, detailExamPage + 1))}
                        disabled={detailExamPage === totalDetailExamPages}
                      >
                        Berikutnya →
                      </button>
                    </div>
                  )}
                </div>
              </>
            )}
          </div>

          {/* === Riwayat Psikotest === */}
          <div className="section-block">
            <h4>Riwayat Psikotest ({candidateDetail.psychotest_attempts.length})</h4>
            {candidateDetail.psychotest_attempts.length === 0 ? (
              <div className="empty-state">Belum ada riwayat psikotest</div>
            ) : (
              <div className="sessions-table">
                <table>
                  <thead>
                    <tr>
                      <th>No</th>
                      <th>Foto</th>
                      <th>Status</th>
                      <th>Mulai</th>
                      <th>Selesai</th>
                      <th>Hasil</th>
                      <th>Aksi</th>
                    </tr>
                  </thead>
                  <tbody>
                    {candidateDetail.psychotest_attempts.map((attempt, idx) => {
                      const statusLabel =
                        attempt.status === 'completed'
                          ? 'Selesai'
                          : attempt.status === 'active'
                            ? 'Sedang Berlangsung'
                            : attempt.status === 'timeout'
                              ? 'Timeout'
                              : attempt.status === 'disqualified'
                                ? 'Diskualifikasi'
                                : attempt.status;

                      const resultSummary = attempt.status === 'completed' && attempt.result
                        ? formatPsychotestResult(attempt.result).summary
                        : attempt.result
                          ? 'Hasil tersedia'
                          : 'Belum ada hasil';

                      return (
                        <tr key={attempt.id}>
                          <td>{idx + 1}</td>
                          <td>
                            {attempt.photo ? (
                              <img
                                src={buildUploadUrl('tmp', attempt.photo)}
                                alt="Foto awal psikotest"
                                className="thumb-photo"
                              />
                            ) : (
                              '-'
                            )}
                          </td>
                          <td>{statusLabel}</td>
                          <td>{attempt.started_at ? new Date(attempt.started_at).toLocaleString('id-ID') : '-'}</td>
                          <td>{attempt.completed_at ? new Date(attempt.completed_at).toLocaleString('id-ID') : '-'}</td>
                          <td>{resultSummary}</td>
                          <td>
                            <button
                              type="button"
                              className="btn-secondary"
                              onClick={() => openPsychotestAttemptDetail(attempt)}
                              disabled={!attempt.answers || attempt.answers.length === 0}
                            >
                              Detail
                            </button>
                          </td>
                        </tr>
                      );
                    })}
                  </tbody>
                </table>
              </div>
            )}
          </div>
        </div>
      ) : (
        <div className="empty-state">Detail kandidat tidak tersedia</div>
      )}
    </div>
  </div>
)}

              {showPsychotestDetailModal && psychotestDetail && (
                <div className="form-modal" onClick={closePsychotestDetailModal}>
                  <div className="form-box large modal-box" onClick={(e) => e.stopPropagation()}>
                    <div className="modal-header">
                      <h3>Detail Psikotest</h3>
                      <button className="modal-close-btn" onClick={closePsychotestDetailModal}>✕</button>
                    </div>

                    <div className="detail-grid">
                      <div>
                        <p className="detail-label">Status</p>
                        <p className="detail-value">
                          {psychotestDetail.status === 'completed'
                            ? 'Selesai'
                            : psychotestDetail.status === 'active'
                              ? 'Sedang Berlangsung'
                              : psychotestDetail.status === 'timeout'
                                ? 'Timeout'
                                : psychotestDetail.status === 'disqualified'
                                  ? 'Diskualifikasi'
                                  : psychotestDetail.status}
                        </p>
                      </div>
                      <div>
                        <p className="detail-label">Mulai</p>
                        <p className="detail-value">{psychotestDetail.started_at ? new Date(psychotestDetail.started_at).toLocaleString('id-ID') : '-'}</p>
                      </div>
                      <div>
                        <p className="detail-label">Selesai</p>
                        <p className="detail-value">{psychotestDetail.completed_at ? new Date(psychotestDetail.completed_at).toLocaleString('id-ID') : '-'}</p>
                      </div>
                      <div>
                        <p className="detail-label">Ringkasan</p>
                        <p className="detail-value">{psychotestDetail.result ? formatPsychotestResult(psychotestDetail.result).summary : '-'}</p>
                      </div>
                    </div>

                    <div className="bulk-actions" style={{ marginBottom: '12px', marginTop: '12px' }}>
                      <div className="bulk-actions-left">
                        <button type="button" className="btn-primary" onClick={downloadPsychotestDetailPdf}>
                          📄 Download PDF Detail Psikotest
                        </button>
                      </div>
                    </div>

                    {psychotestDetail.answers?.length > 0 ? (
                      <div className="sessions-table" style={{ marginTop: '16px' }}>
                        <table>
                          <thead>
                            <tr>
                              <th>No</th>
                              <th>Pertanyaan A</th>
                              <th>Pertanyaan B</th>
                              <th>Jawaban</th>
                            </tr>
                          </thead>
                          <tbody>
                            {psychotestDetail.answers.map((answer, idx) => (
                              <tr key={`${psychotestDetail.id}-${answer.question_id}`}>
                                <td>{idx + 1}</td>
                                <td>
                                  <div style={{ fontWeight: 600 }}>{answer.dimension_a || '-'}</div>
                                  <div className="text-sm">{answer.statement_a || '-'}</div>
                                </td>
                                <td>
                                  <div style={{ fontWeight: 600 }}>{answer.dimension_b || '-'}</div>
                                  <div className="text-sm">{answer.statement_b || '-'}</div>
                                </td>
                                <td>
                                  {String(answer.answer_value || '').toLowerCase() === 'a'
                                    ? 'A'
                                    : String(answer.answer_value || '').toLowerCase() === 'b'
                                      ? 'B'
                                      : String(answer.answer_value || '-').toUpperCase()}
                                </td>
                              </tr>
                            ))}
                          </tbody>
                        </table>
                      </div>
                    ) : (
                      <div className="empty-state">Detail jawaban psikotest belum tersedia</div>
                    )}
                  </div>
                </div>
              )}

              {showSessionDetailModal && (
                <div className="form-modal" onClick={() => setShowSessionDetailModal(false)}>
                  <div className="form-box large modal-box" onClick={(e) => e.stopPropagation()}>
                    <div className="modal-header">
                      <h3>Detail Jawaban Ujian</h3>
                      <button className="modal-close-btn" onClick={() => setShowSessionDetailModal(false)}>✕</button>
                    </div>

                    {sessionDetailLoading ? (
                      <div className="loading-text">Memuat detail jawaban...</div>
                    ) : sessionDetail ? (
                      <div className="modal-body">
                        <div className="bulk-actions" style={{ marginBottom: '12px' }}>
                          <div className="bulk-actions-left">
                            <button type="button" className="btn-primary" onClick={downloadSessionDetailPdf}>
                              📄 Download PDF Detail Jawaban
                            </button>
                          </div>
                        </div>

                        <div className="detail-grid">
                          <div>
                            <p className="detail-label">Nama</p>
                            <p className="detail-value">{sessionDetail.session?.candidate_name || '-'}</p>
                          </div>
                          <div>
                            <p className="detail-label">Email</p>
                            <p className="detail-value">{sessionDetail.session?.candidate_email || '-'}</p>
                          </div>
                          <div>
                            <p className="detail-label">Ujian</p>
                            <p className="detail-value">{sessionDetail.session?.exam_title || '-'}</p>
                          </div>
                          <div>
                            <p className="detail-label">Status</p>
                            <p className="detail-value">{sessionDetail.session?.status || '-'}</p>
                          </div>
                          <div>
                            <p className="detail-label">Skor</p>
                            <p className="detail-value">{sessionDetail.session?.score != null ? `${sessionDetail.session.score}%` : '-'}</p>
                          </div>
                          <div>
                            <p className="detail-label">Foto Sesi</p>
                            <p className="detail-value">{sessionDetail.session?.photo ? 'Ada' : 'Tidak ada'}</p>
                          </div>
                        </div>

                        {sessionDetail.session?.photo && (
                          <div className="section-block">
                            <h4>Foto Saat Ujian</h4>
                            <div className="preview-box" style={{ maxWidth: '320px' }}>
                              <img
                                src={buildUploadUrl('face', sessionDetail.session.photo)}
                                alt="Foto sesi ujian"
                                style={{ width: '100%', display: 'block', borderRadius: '8px' }}
                              />
                            </div>
                          </div>
                        )}

                        <div className="section-block">
                          <h4>Jawaban Kandidat ({sessionDetail.answers?.length || 0})</h4>
                          {sessionDetail.answers?.length > 0 ? (
                            <div className="questions-table-container">
                              <table className="questions-table">
                                <thead>
                                  <tr>
                                    <th style={{ width: '50px' }}>No</th>
                                    <th>Pertanyaan</th>
                                    <th style={{ width: '90px' }}>Foto</th>
                                    <th>Jawaban Kandidat</th>
                                    <th style={{ width: '90px' }}>Kunci</th>
                                    <th style={{ width: '90px' }}>Status</th>
                                  </tr>
                                </thead>
                                <tbody>
                                  {sessionDetail.answers.map((answer, idx) => (
                                    <tr key={`${answer.question_id}-${idx}`}>
                                      <td>{idx + 1}</td>
                                      <td>{answer.question_text || '-'}</td>
                                      <td>
                                        {answer.photo ? (
                                          <img
                                            src={buildUploadUrl('questions', answer.photo)}
                                            alt="Foto soal"
                                            style={{ width: '72px', height: '54px', objectFit: 'cover', borderRadius: '6px', border: '1px solid var(--border)' }}
                                          />
                                        ) : (
                                          '-'
                                        )}
                                      </td>
                                      <td>{answer.correct_answer ? ((answer.selected_option || '-').toString().toUpperCase()) : (answer.selected_option || '-')}</td>
                                      <td>{answer.correct_answer ? String(answer.correct_answer).toUpperCase() : '-'}</td>
                                      <td>{answer.correct_answer ? (Number(answer.is_correct) === 1 ? 'Benar' : 'Salah') : 'Essay'}</td>
                                    </tr>
                                  ))}
                                </tbody>
                              </table>
                            </div>
                          ) : (
                            <div className="empty-state">Belum ada jawaban tersimpan</div>
                          )}
                        </div>

                        <div className="section-block">
                          <h4>Pelanggaran ({sessionDetail.violations?.length || 0})</h4>
                          {sessionDetail.violations?.length > 0 ? (
                            <div className="detail-list">
                              {sessionDetail.violations.map((violation) => (
                                <div key={violation.id} className="detail-item">
                                  <div>
                                    <strong>{VIOLATION_LABELS[violation.violation_type] || violation.violation_type || '-'}</strong>
                                    <p className="text-sm">{violation.description || '-'}</p>
                                  </div>
                                  <div className="detail-item-right">
                                    <span>{violation.logged_at ? new Date(violation.logged_at).toLocaleString('id-ID') : '-'}</span>
                                  </div>
                                </div>
                              ))}
                            </div>
                          ) : (
                            <div className="empty-state">Tidak ada pelanggaran tercatat</div>
                          )}
                        </div>
                      </div>
                    ) : (
                      <div className="empty-state">Detail jawaban tidak tersedia</div>
                    )}
                  </div>
                </div>
              )}

            </div>
          </>
        )}

        {/* ─── EXAMS TAB ──────────────────────────────────────────────────────*/}
        {activeTab === 'exams' && (
          <div className="exam-mgmt">
            <div className="mgmt-header">
              <h2>Kelola Ujian</h2>
              <button className="btn-add" onClick={() => {
                setEditingExam(null);
                setExamForm({ title: '', description: '', duration_minutes: 60, max_violations: 1, total: 0, multiple_choice: 1, passing_grade: 60, position: 'all' });
                setShowExamForm(true);
              }}>
                ➕ Tambah Ujian Baru
              </button>
            </div>

            {showExamForm && (
              <div className="form-modal">
                <div className="form-box">
                  <div className="modal-header">
                    <h3>{editingExam ? 'Edit Ujian' : 'Ujian Baru'}</h3>
                    <button className="modal-close-btn" onClick={() => setShowExamForm(false)}>✕</button>
                  </div>
                  <div className="form-group">
                    <label>Judul Ujian</label>
                    <input
                      type="text"
                      value={examForm.title}
                      onChange={(e) => setExamForm({...examForm, title: e.target.value})}
                      placeholder="Contoh: Tes Kemampuan Umum"
                    />
                  </div>
                  <div className="form-group">
                    <label>Deskripsi</label>
                    <textarea
                      value={examForm.description}
                      onChange={(e) => setExamForm({...examForm, description: e.target.value})}
                      placeholder="Deskripsi ujian..."
                      rows="3"
                    />
                  </div>
                  <div className="form-row">
                    <div className="form-group">
                      <label>Durasi (menit)</label>
                      <input
                        type="number"
                        value={examForm.duration_minutes}
                        onChange={(e) => setExamForm({...examForm, duration_minutes: parseInt(e.target.value)})}
                        min="1"
                      />
                    </div>
                    <div className="form-group">
                      <label>Maks Pelanggaran</label>
                      <input
                        type="number"
                        value={examForm.max_violations}
                        onChange={(e) => setExamForm({...examForm, max_violations: parseInt(e.target.value)})}
                        min="1"
                      />
                    </div>
                    <div className="form-group">
                      <label>Posisi</label>
                      <select
                        value={examForm.position}
                        onChange={(e) => setExamForm({...examForm, position: e.target.value})}
                      >
                        <option value="all">Semua Posisi</option>
                        <option value="software">Software Developer</option>
                        <option value="hardware">Hardware Engineer</option>
                        <option value="general">General</option>
                      </select>
                    </div>
                  </div>
                  
                  <div className="form-group">
                    <label>Nilai Kelulusan Minimum (%)</label>
                    <input
                      type="number"
                      value={examForm.passing_grade}
                      onChange={(e) => setExamForm({...examForm, passing_grade: parseFloat(e.target.value)})}
                      min="0"
                      max="100"
                      step="0.01"
                      placeholder="Contoh: 60"
                    />
                  </div>
                  <div className="form-group">
                    <label>Total Soal Ditampilkan ke Kandidat</label>
                    <input
                      type="number"
                      value={examForm.total}
                      onChange={(e) => setExamForm({ ...examForm, total: Math.max(0, parseInt(e.target.value || '0', 10)) })}
                      min="0"
                      step="1"
                      placeholder="0 = tampilkan semua soal"
                    />
                    <small style={{ display: 'block', marginTop: '6px', color: 'var(--text-muted)' }}>
                      Isi 0 untuk menampilkan semua soal yang tersedia pada bank soal.
                    </small>
                  </div>
                  <div className="form-actions">
                    <button className="btn-save" onClick={saveExam}>Simpan</button>
                    <button className="btn-cancel" onClick={() => setShowExamForm(false)}>Batal</button>
                  </div>
                </div>
              </div>
            )}

            {loadingExams ? (
              <div className="loading-text">Memuat ujian...</div>
            ) : (
              <div className="exam-list">
                {exams.map(exam => (
                  <div key={exam.id} className="exam-item">
                    <div className="exam-info">
                      <h3>{exam.title}</h3>
                      <p className="desc">{exam.description}</p>
                      <div className="meta">
                        <span>⏱ {exam.duration_minutes} menit</span>
                        <span>❓ Tampil ke kandidat: {exam.total > 0 ? Math.min(exam.total, exam.total_questions) : exam.total_questions} soal</span>
                        <span>📚 Bank soal: {exam.total_questions} soal</span>
                        <span>{exam.multiple_choice === 1 ? '📝' : '✍️'} {exam.multiple_choice === 1 ? 'Pilihan Ganda' : 'Essay'}</span>
                        <span>⚠️ Max {exam.max_violations} pelanggaran</span>
                        <span>✅ Min Score: {exam.passing_grade}%</span>
                        <span>� Posisi: {exam.position === 'all' ? 'Semua Posisi' : exam.position}</span>
                        <span>�👤 Dibuat oleh: {exam.created_by_name}</span>
                      </div>
                    </div>
                    <div className="exam-actions">
                      <button className="btn-primary" onClick={() => openExamQuestionsModal(exam)}>
                        ▶ Lihat Soal
                      </button>
                      <button className="btn-secondary" onClick={() => startEditExam(exam)}>Edit</button>
                      <button className="btn-danger" onClick={() => deleteExam(exam.id)}>Hapus</button>
                    </div>

                    {/* Questions section */}
                    {expandedExams[exam.id] && selectedExamForQuestions === exam.id && (
                      <div className="questions-section">
                        <div className="questions-header">
                          <div>
                            <h4>Soal untuk: {exam.title}</h4>
                            <p style={{fontSize: '12px', color: 'var(--text-muted)', marginTop: '4px'}}>
                              Tipe: {exam.multiple_choice === 1 ? '📝 Pilihan Ganda' : '✍️ Essay'}
                            </p>
                          </div>
                          <div style={{display: 'flex', gap: '8px'}}>
                            <button className="btn-info" onClick={() => { 
                              setUploadInstructionsType(exam.multiple_choice === 1 ? 'multiple-choice' : 'essay'); 
                              setShowUploadInstructions(true); 
                            }}>
                              ℹ️ Panduan Upload
                            </button>
                            <button className="btn-add-question" onClick={() => {
                              setEditingQuestion(null);
                              setQuestionForm({
                                question_text: '',
                                option_a: '',
                                option_b: '',
                                option_c: '',
                                option_d: '',
                                option_e: '',
                                correct_answer: 'a',
                                points: 10
                              });
                              setShowQuestionForm(true);
                            }}>
                              ➕ Tambah Soal
                            </button>
                          </div>
                        </div>

                        {showQuestionForm && selectedExamForQuestions === exam.id && (
                          <div className="form-modal">
                            <div className="form-box large">
                              <div className="modal-header">
                                <h3>
                                  {editingQuestion ? 'Edit Soal' : 'Soal Baru'}
                                  <span style={{fontSize: '13px', fontWeight: 400, color: 'var(--text-muted)', marginLeft: '10px'}}>
                                    {exam.multiple_choice === 1 ? '📝 Pilihan Ganda' : '✍️ Essay'}
                                  </span>
                                </h3>
                                <button className="modal-close-btn" onClick={() => setShowQuestionForm(false)}>✕</button>
                              </div>
                              <div className="form-group">
                                <label>Pertanyaan</label>
                                <textarea
                                  value={questionForm.question_text}
                                  onChange={(e) => setQuestionForm({...questionForm, question_text: e.target.value})}
                                  placeholder="Masukkan pertanyaan..."
                                  rows="3"
                                />
                              </div>

                              {exam.multiple_choice === 1 ? (
                                <>
                                  <div className="form-group">
                                    <label>Opsi A</label>
                                    <input type="text" value={questionForm.option_a} onChange={(e) => setQuestionForm({...questionForm, option_a: e.target.value})} placeholder="Opsi A" />
                                  </div>
                                  <div className="form-group">
                                    <label>Opsi B</label>
                                    <input type="text" value={questionForm.option_b} onChange={(e) => setQuestionForm({...questionForm, option_b: e.target.value})} placeholder="Opsi B" />
                                  </div>
                                  <div className="form-group">
                                    <label>Opsi C</label>
                                    <input type="text" value={questionForm.option_c} onChange={(e) => setQuestionForm({...questionForm, option_c: e.target.value})} placeholder="Opsi C" />
                                  </div>
                                  <div className="form-group">
                                    <label>Opsi D</label>
                                    <input type="text" value={questionForm.option_d} onChange={(e) => setQuestionForm({...questionForm, option_d: e.target.value})} placeholder="Opsi D" />
                                  </div>
                                  <div className="form-group">
                                    <label>Opsi E <span style={{fontWeight:400, color:'var(--text-muted)'}}>(opsional)</span></label>
                                    <input type="text" value={questionForm.option_e} onChange={(e) => setQuestionForm({...questionForm, option_e: e.target.value})} placeholder="Opsi E (kosongkan jika tidak ada)" />
                                  </div>
                                  <div className="form-row">
                                    <div className="form-group">
                                      <label>Jawaban Benar</label>
                                      <select value={questionForm.correct_answer} onChange={(e) => setQuestionForm({...questionForm, correct_answer: e.target.value})}>
                                        <option value="a">A</option>
                                        <option value="b">B</option>
                                        <option value="c">C</option>
                                        <option value="d">D</option>
                                        <option value="e">E</option>
                                      </select>
                                    </div>
                                    <div className="form-group">
                                      <label>Poin</label>
                                      <input type="number" value={questionForm.points} onChange={(e) => setQuestionForm({...questionForm, points: parseInt(e.target.value)})} min="1" />
                                    </div>
                                  </div>
                                </>
                              ) : (
                                <>
                                  <div className="essay-info-box">
                                    <span>✍️</span>
                                    <div>
                                      <strong>Soal Essay</strong>
                                      <p>Recruiter akan mengoreksi dan memberikan nilai secara manual setelah kandidat mengerjakan ujian.</p>
                                    </div>
                                  </div>
                                  <div className="form-group">
                                    <label>Rubrik Penilaian <span style={{fontWeight:400, color:'var(--text-muted)'}}>(panduan untuk recruiter)</span></label>
                                    <textarea
                                      value={questionForm.option_a}
                                      onChange={(e) => setQuestionForm({...questionForm, option_a: e.target.value})}
                                      placeholder="Contoh: Jawaban lengkap dan tepat: 100%, Sebagian benar: 50-70%, Tidak relevan: 0%"
                                      rows="3"
                                    />
                                  </div>
                                  <div className="form-group">
                                    <label>Poin Maksimal</label>
                                    <input type="number" value={questionForm.points} onChange={(e) => setQuestionForm({...questionForm, points: parseInt(e.target.value)})} min="1" />
                                  </div>
                                </>
                              )}

                              <div className="form-group">
                                <label>Foto Soal <span style={{fontWeight:400, color:'var(--text-muted)'}}>(opsional)</span></label>
                                <input
                                  type="file"
                                  accept="image/*"
                                  onChange={(e) => {
                                    const file = e.target.files?.[0] || null;
                                    setQuestionPhotoFile(file);
                                    if (file) {
                                      const reader = new FileReader();
                                      reader.onload = () => setQuestionPhotoPreview(reader.result);
                                      reader.readAsDataURL(file);
                                    } else {
                                      setQuestionPhotoPreview(null);
                                    }
                                  }}
                                />
                                {questionPhotoPreview && (
                                  <div className="preview-box">
                                    <img src={questionPhotoPreview} alt="Preview soal" />
                                  </div>
                                )}
                              </div>
                              <div className="form-actions">
                                <button className="btn-save" onClick={saveQuestion}>Simpan</button>
                                <button className="btn-cancel" onClick={() => setShowQuestionForm(false)}>Batal</button>
                              </div>
                            </div>
                          </div>
                        )}

                        {loadingQuestions ? (
                          <div className="loading-text">Memuat soal...</div>
                        ) : (
                          <div>
                            <div className="bulk-actions">
                              <div className="bulk-actions-left">
                                <label className="checkbox-label">
                                  <input
                                    type="checkbox"
                                    checked={selectAllQuestions}
                                    onChange={toggleSelectAllQuestions}
                                  />
                                  Pilih Semua
                                </label>
                                <button className="btn-danger" onClick={bulkDeleteSelectedQuestions}>Hapus Terpilih</button>
                              </div>
                            </div>

                            <div className="bulk-actions">
                              <label className="upload-label">
                                📤 Upload Excel soal:
                                <input
                                  type="file"
                                  accept=".xlsx,.xls"
                                  onChange={(e) => setExcelFile(e.target.files?.[0] || null)}
                                />
                              </label>
                              <button className="btn-primary" onClick={uploadQuestionsExcelFile}>Upload Excel</button>
                            </div>

                            <div className="bulk-actions">
                              <div className="bulk-actions-left">
                                <a className="btn-info link-btn" href="/templates/Template_Exam_Pilihan_Ganda.xlsx" download>
                                  ⬇️ Template Exam Pilihan Ganda
                                </a>
                               
                                <label className="upload-label">
                                  Show List:
                                  <select value={questionPerPage} onChange={(e) => setQuestionPerPage(e.target.value)}>
                                    <option value="10">10</option>
                                    <option value="25">25</option>
                                    <option value="50">50</option>
                                    <option value="all">All</option>
                                  </select>
                                </label>
                                <button type="button" className="btn-info" onClick={exportExamQuestionsExcel}>
                                  ⬇️ Export Excel
                                </button>
                              </div>
                            </div>

                            {questions.length === 0 ? (
                              <div className="empty-state">Tidak ada soal. Buat soal baru untuk ujian ini.</div>
                            ) : (
                              <div className="questions-table-container">
                                <table className="questions-table">
                                  <thead>
                                    <tr>
                                      <th style={{width: '30px'}}>
                                        <input
                                          type="checkbox"
                                          checked={selectAllQuestions}
                                          onChange={toggleSelectAllQuestions}
                                        />
                                      </th>
                                      <th style={{width: '50px'}}>No</th>
                                      <th>Pertanyaan</th>
                                      <th style={{width: '100px'}}>Tipe</th>
                                      <th>Opsi & Jawaban</th>
                                      <th style={{width: '60px'}}>Poin</th>
                                      <th style={{width: '80px'}}>Aksi</th>
                                    </tr>
                                  </thead>
                                  <tbody>
                                    {paginatedQuestions.map((q, idx) => (
                                      <tr key={q.id} className={selectedQuestionIds.includes(q.id) ? 'selected' : ''}>
                                        <td>
                                          <input
                                            type="checkbox"
                                            checked={selectedQuestionIds.includes(q.id)}
                                            onChange={(e) => toggleQuestionSelection(q.id, e.target.checked)}
                                          />
                                        </td>
                                        <td>{((questionPage - 1) * effectiveQuestionPerPage) + idx + 1}</td>
                                        <td>
                                          <div className="td-question">
                                            {q.photo && <span className="q-photo-badge">📸</span>}
                                            <span className="q-text-preview">{q.question_text.substring(0, 80)}{q.question_text.length > 80 ? '...' : ''}</span>
                                          </div>
                                        </td>
                                        <td>
                                          <span className={`badge badge-${q.correct_answer ? 'mc' : 'essay'}`}>
                                            {q.correct_answer ? '📝 Pilihan Ganda' : '✍️ Essay'}
                                          </span>
                                        </td>
                                        <td>
                                          {q.correct_answer ? (
                                            <div className="td-options">
                                              {['a', 'b', 'c', 'd', 'e'].map(opt => {
                                                const value = q[`option_${opt}`];
                                                if (!value) return null;
                                                return (
                                                  <div key={opt} className={`option-item ${q.correct_answer === opt ? 'correct-option' : ''}`}>
                                                    <strong>{opt.toUpperCase()}:</strong> {value.substring(0, 40)}{value.length > 40 ? '...' : ''}
                                                  </div>
                                                );
                                              })}
                                            </div>
                                          ) : (
                                            <div>
                                              <span className="essay-label">✍️ Essay — Recruiter menilai</span>
                                              {q.option_a && (
                                                <div style={{fontSize: '11px', color: 'var(--text-muted)', marginTop: '4px'}}>
                                                  📋 Rubrik: {q.option_a.substring(0, 60)}{q.option_a.length > 60 ? '...' : ''}
                                                </div>
                                              )}
                                            </div>
                                          )}
                                        </td>
                                        <td><strong>{q.points}</strong></td>
                                        <td>
                                          <div className="td-actions">
                                            <button className="btn-sm btn-secondary" onClick={() => startEditQuestion(q)}>Edit</button>
                                            <button className="btn-sm btn-danger" onClick={() => deleteQuestion(q.id)}>Hapus</button>
                                          </div>
                                        </td>
                                      </tr>
                                    ))}
                                  </tbody>
                                </table>

                                {/* Pagination */}
                                {totalPages > 1 && (
                                  <div className="pagination">
                                    <button 
                                      className="pagination-btn"
                                      onClick={() => setQuestionPage(Math.max(1, questionPage - 1))}
                                      disabled={questionPage === 1}
                                    >
                                      ← Sebelumnya
                                    </button>
                                    <div className="pagination-info">
                                      Halaman {questionPage} dari {totalPages} (Total: {questions.length} soal)
                                    </div>
                                    <button 
                                      className="pagination-btn"
                                      onClick={() => setQuestionPage(Math.min(totalPages, questionPage + 1))}
                                      disabled={questionPage === totalPages}
                                    >
                                      Berikutnya →
                                    </button>
                                  </div>
                                )}
                              </div>
                            )}
                          </div>
                        )}
                      </div>
                    )}
                  </div>
                ))}
                {exams.length === 0 && <div className="empty-state">Tidak ada ujian. Buat ujian baru untuk memulai.</div>}
              </div>
            )}
          </div>
        )}

        {activeTab === 'psychotest' && (
          <div className="exam-mgmt">
            <div className="mgmt-header">
              <h2>Kelola Soal Psikotest (A/B)</h2>
              <button className="btn-add" onClick={() => {
                resetPsychotestForm();
                setShowPsychotestForm(true);
              }}>
                ➕ Tambah Soal Psikotest
              </button>
            </div>

            {showPsychotestForm && (
              <div className="form-modal">
                <div className="form-box">
                  <div className="modal-header">
                    <h3>{editingPsychotestQuestion ? 'Edit Soal Psikotest' : 'Soal Psikotest Baru'}</h3>
                    <button className="modal-close-btn" onClick={() => setShowPsychotestForm(false)}>✕</button>
                  </div>

                  <div className="form-row">
                    <div className="form-group">
                      <label>Dimensi A</label>
                      <select value={psychotestForm.dimension_a} onChange={(e) => setPsychotestForm({ ...psychotestForm, dimension_a: e.target.value })}>
                        {PSY_DIMENSIONS.map((d) => (
                          <option key={d.value} value={d.value}>{d.label}</option>
                        ))}
                      </select>
                    </div>
                    <div className="form-group">
                      <label>Dimensi B</label>
                      <select value={psychotestForm.dimension_b} onChange={(e) => setPsychotestForm({ ...psychotestForm, dimension_b: e.target.value })}>
                        {PSY_DIMENSIONS.map((d) => (
                          <option key={d.value} value={d.value}>{d.label}</option>
                        ))}
                      </select>
                    </div>
                  </div>

                  <div className="form-group">
                    <label>Pernyataan A</label>
                    <textarea rows="3" value={psychotestForm.statement_a} onChange={(e) => setPsychotestForm({ ...psychotestForm, statement_a: e.target.value })} />
                  </div>

                  <div className="form-group">
                    <label>Pernyataan B</label>
                    <textarea rows="3" value={psychotestForm.statement_b} onChange={(e) => setPsychotestForm({ ...psychotestForm, statement_b: e.target.value })} />
                  </div>

                  <div className="form-group">
                    <label>Status</label>
                    <select value={psychotestForm.is_active} onChange={(e) => setPsychotestForm({ ...psychotestForm, is_active: parseInt(e.target.value, 10) })}>
                      <option value={1}>Aktif</option>
                      <option value={0}>Nonaktif</option>
                    </select>
                  </div>

                  <div className="form-actions">
                    <button className="btn-save" onClick={savePsychotestQuestion}>Simpan</button>
                    <button className="btn-cancel" onClick={() => setShowPsychotestForm(false)}>Batal</button>
                  </div>
                </div>
              </div>
            )}

            <div className="bulk-actions" style={{ marginTop: '4px' }}>
              <div className="bulk-actions-left">
                <label className="upload-label">
                  ⏱ Durasi Psikotest (menit):
                  <input
                    type="number"
                    min="1"
                    max="180"
                    value={psychotestDurationMinutes}
                    onChange={(e) => setPsychotestDurationMinutes(parseInt(e.target.value || '0', 10))}
                    style={{ width: '110px' }}
                  />
                </label>
                <label className="upload-label">
                  🔢 Jumlah Soal Ditampilkan:
                  <input
                    type="number"
                    min="0"
                    max="500"
                    value={psychotestTotalQuestions}
                    onChange={(e) => setPsychotestTotalQuestions(parseInt(e.target.value || '0', 10))}
                    style={{ width: '120px' }}
                  />
                </label>
                <label className="upload-label">
                  ⚠️ Maks Pelanggaran:
                  <input
                    type="number"
                    min="1"
                    max="20"
                    value={psychotestMaxViolations}
                    onChange={(e) => setPsychotestMaxViolations(parseInt(e.target.value || '0', 10))}
                    style={{ width: '110px' }}
                  />
                </label>
                <button className="btn-primary" onClick={savePsychotestDuration} disabled={savingPsychotestDuration}>
                  {savingPsychotestDuration ? 'Menyimpan...' : 'Simpan'}
                </button>
              </div>
            </div>

            <div className="bulk-actions" style={{ marginTop: '4px' }}>
              <div className="bulk-actions-left">
                <label className="checkbox-label">
                  <input
                    type="checkbox"
                    checked={selectAllPsychotestQuestions}
                    onChange={toggleSelectAllPsychotestQuestions}
                  />
                  Pilih Semua
                </label>
                <button className="btn-danger" onClick={bulkDeleteSelectedPsychotestQuestions}>Hapus Terpilih</button>
              </div>
            </div>

            <div className="bulk-actions" style={{ marginTop: '4px' }}>
              <label className="upload-label">
                📤 Upload Excel Soal Psikotest (A/B):
                <input
                  type="file"
                  accept=".xlsx,.xls"
                  onChange={(e) => setPsychotestExcelFile(e.target.files?.[0] || null)}
                />
              </label>
              <button className="btn-primary" onClick={uploadPsychotestExcelFile}>Upload Excel</button>
              
            </div>

            <div className="bulk-actions">
              <div className="bulk-actions-left">
                <a className="btn-info link-btn" href="/templates/Template_Psikotest_AB.xlsx" download>
                  ⬇️ Template Psikotest A/B
                </a>
                <label className="upload-label">
                  Show List:
                  <select value={psychotestPerPage} onChange={(e) => setPsychotestPerPage(e.target.value)}>
                    <option value="10">10</option>
                    <option value="25">25</option>
                    <option value="50">50</option>
                    <option value="all">All</option>
                  </select>
                </label>
                <button type="button" className="btn-info" onClick={exportPsychotestExcel}>
                  ⬇️ Export Excel
                </button>
              </div>
            </div>

            <div style={{fontSize: '12px', color: 'var(--text-muted)', marginBottom: '12px'}}>
              Format Excel disarankan: Kolom A=Dimensi A, B=Pernyataan A, C=Dimensi B, D=Pernyataan B, E=Status (opsional: aktif/nonaktif)
            </div>

            {loadingPsychotest ? (
              <div className="loading-text">Memuat soal psikotest...</div>
            ) : (
              <div className="questions-table-container">
                <table className="questions-table">
                  <thead>
                    <tr>
                      <th style={{width: '40px'}}>
                        <input
                          type="checkbox"
                          checked={selectAllPsychotestQuestions}
                          onChange={toggleSelectAllPsychotestQuestions}
                        />
                      </th>
                      <th style={{width: '50px'}}>ID</th>
                      <th>Dimensi A</th>
                      <th>Pernyataan A</th>
                      <th>Dimensi B</th>
                      <th>Pernyataan B</th>
                      <th style={{width: '80px'}}>Status</th>
                      <th style={{width: '100px'}}>Aksi</th>
                    </tr>
                  </thead>
                  <tbody>
                    {paginatedPsychotestQuestions.map((q) => (
                      <tr key={q.id} className={selectedPsychotestQuestionIds.includes(q.id) ? 'selected' : ''}>
                        <td>
                          <input
                            type="checkbox"
                            checked={selectedPsychotestQuestionIds.includes(q.id)}
                            onChange={(e) => togglePsychotestQuestionSelection(q.id, e.target.checked)}
                          />
                        </td>
                        <td>{q.id}</td>
                        <td>{PSY_DIMENSIONS.find((d) => d.value === q.dimension_a)?.label || q.dimension_a}</td>
                        <td>{q.statement_a}</td>
                        <td>{PSY_DIMENSIONS.find((d) => d.value === q.dimension_b)?.label || q.dimension_b}</td>
                        <td>{q.statement_b}</td>
                        <td>{Number(q.is_active) === 1 ? 'Aktif' : 'Nonaktif'}</td>
                        <td>
                          <div className="td-actions">
                            <button className="btn-sm btn-secondary" onClick={() => startEditPsychotestQuestion(q)}>Edit</button>
                            <button className="btn-sm btn-danger" onClick={() => deletePsychotestQuestion(q.id)}>Hapus</button>
                          </div>
                        </td>
                      </tr>
                    ))}
                    {psychotestQuestions.length === 0 && (
                      <tr><td colSpan="8" className="empty-row">Belum ada soal psikotest</td></tr>
                    )}
                  </tbody>
                </table>

                {psychotestQuestions.length > 0 && totalPsychotestPages > 1 && (
                  <div className="pagination">
                    <button
                      className="pagination-btn"
                      onClick={() => setPsychotestPage(Math.max(1, psychotestPage - 1))}
                      disabled={psychotestPage === 1}
                    >
                      ← Sebelumnya
                    </button>
                    <div className="pagination-info">
                      Halaman {psychotestPage} dari {totalPsychotestPages} (Total: {psychotestQuestions.length} soal)
                    </div>
                    <button
                      className="pagination-btn"
                      onClick={() => setPsychotestPage(Math.min(totalPsychotestPages, psychotestPage + 1))}
                      disabled={psychotestPage === totalPsychotestPages}
                    >
                      Berikutnya →
                    </button>
                  </div>
                )}
              </div>
            )}
          </div>
        )}

        {activeTab === 'recruiters' && user?.role === 'admin' && (
          <div className="exam-mgmt">
            <div className="mgmt-header">
              <h2>Kelola Recruiter</h2>
              <button className="btn-add" onClick={() => openRecruiterForm()}>
                ➕ Tambah Recruiter
              </button>
            </div>

            {showRecruiterForm && (
              <div className="form-modal">
                <div className="form-box" onClick={(e) => e.stopPropagation()}>
                  <div className="modal-header">
                    <h3>{editingRecruiterId ? 'Edit Recruiter' : 'Recruiter Baru'}</h3>
                    <button className="modal-close-btn" onClick={() => setShowRecruiterForm(false)}>✕</button>
                  </div>
                  <div className="form-group">
                    <label>Nama</label>
                    <input
                      type="text"
                      value={recruiterForm.name}
                      onChange={(e) => setRecruiterForm({ ...recruiterForm, name: e.target.value })}
                      placeholder="Nama recruiter"
                    />
                  </div>
                  <div className="form-group">
                    <label>Email</label>
                    <input
                      type="email"
                      value={recruiterForm.email}
                      onChange={(e) => setRecruiterForm({ ...recruiterForm, email: e.target.value })}
                      placeholder="email@company.com"
                    />
                  </div>
                  <div className="form-group">
                    <label>Password {editingRecruiterId ? <span style={{fontWeight:400, color:'var(--text-muted)'}}>(opsional, isi jika ingin ganti)</span> : ''}</label>
                    <input
                      type="password"
                      value={recruiterForm.password}
                      onChange={(e) => setRecruiterForm({ ...recruiterForm, password: e.target.value })}
                      placeholder={editingRecruiterId ? 'Kosongkan jika tidak diubah' : 'Minimal 6 karakter'}
                    />
                  </div>
                  <div className="form-actions">
                    <button className="btn-save" onClick={saveRecruiter}>Simpan</button>
                    <button className="btn-cancel" onClick={() => setShowRecruiterForm(false)}>Batal</button>
                  </div>
                </div>
              </div>
            )}

            {loadingRecruiters ? (
              <div className="loading-text">Memuat data recruiter...</div>
            ) : (
              <div className="sessions-table">
                <table>
                  <thead>
                    <tr>
                      <th>No</th>
                      <th>Nama</th>
                      <th>Email</th>
                      <th>Role</th>
                      <th>Aksi</th>
                    </tr>
                  </thead>
                  <tbody>
                    {recruiters.map((rec, idx) => (
                      <tr key={rec.id}>
                        <td>{idx + 1}</td>
                        <td>{rec.name}</td>
                        <td>{rec.email}</td>
                        <td>{rec.role}</td>
                        <td>
                          <div className="td-actions-row">
                            <button className="btn-secondary" onClick={() => openRecruiterForm(rec)}>Edit</button>
                            <button className="btn-danger" onClick={() => removeRecruiter(rec.id)}>Hapus</button>
                          </div>
                        </td>
                      </tr>
                    ))}
                    {recruiters.length === 0 && (
                      <tr><td colSpan="5" className="empty-row">Belum ada recruiter</td></tr>
                    )}
                  </tbody>
                </table>
              </div>
            )}
          </div>
        )}
        {showUploadInstructions && (
          <div className="form-modal" style={{ zIndex: 1101 }} onClick={() => setShowUploadInstructions(false)}>
            <div className="form-box large modal-box" onClick={(e) => e.stopPropagation()}>
              <div style={{display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', marginBottom: '24px', paddingBottom: '16px', borderBottom: '1px solid var(--border)'}}>
                <div style={{textAlign: 'left', flex: 1}}>
                  <h3 style={{marginBottom: '8px', marginTop: 0}}>
                    {uploadInstructionsType === 'multiple-choice' ? '📝 Panduan Upload Soal Pilihan Ganda' : '✍️ Panduan Upload Soal Essay'}
                  </h3>
                  <p style={{color: 'var(--text-muted)', fontSize: '13px', margin: 0}}>
                    Ikuti format di bawah ini untuk upload Excel soal
                  </p>
                </div>
                <button 
                  type="button"
                  onClick={() => setShowUploadInstructions(false)}
                  className="modal-close-btn"
                >
                  ✕
                </button>
              </div>
              {uploadInstructionsType === 'multiple-choice' ? (
                <div className="upload-guide">
                  <div className="guide-section">
                    <h4>📋 Format File Excel</h4>
                    <p>File harus berformat <strong>.xlsx</strong> atau <strong>.xls</strong> dengan kolom berikut:</p>
                    <div className="guide-columns">
                      <div className="column-item"><strong>Kolom A</strong><br/>Pertanyaan</div>
                      <div className="column-item"><strong>Kolom B</strong><br/>Opsi A</div>
                      <div className="column-item"><strong>Kolom C</strong><br/>Opsi B</div>
                      <div className="column-item"><strong>Kolom D</strong><br/>Opsi C</div>
                      <div className="column-item"><strong>Kolom E</strong><br/>Opsi D</div>
                      <div className="column-item"><strong>Kolom F</strong><br/>Opsi E (opsional)</div>
                      <div className="column-item"><strong>Kolom G</strong><br/>Jawaban (a/b/c/d/e)</div>
                      <div className="column-item"><strong>Kolom H</strong><br/>Poin</div>
                    </div>
                  </div>
                  <div className="guide-section">
                    <h4>📊 Contoh Data</h4>
                    <table className="example-table">
                      <thead>
                        <tr>
                          <th>Pertanyaan</th>
                          <th>A</th>
                          <th>B</th>
                          <th>C</th>
                          <th>D</th>
                          <th>E</th>
                          <th>Kunci</th>
                          <th>Poin</th>
                        </tr>
                      </thead>
                      <tbody>
                        <tr>
                          <td>Contoh pertanyaan 1</td>
                          <td>Option A</td>
                          <td>Option B</td>
                          <td>Option C</td>
                          <td>Option D</td>
                          <td>Option E</td>
                          <td><strong>a</strong></td>
                          <td>5</td>
                        </tr>
                        <tr>
                          <td>Contoh pertanyaan 2</td>
                          <td>Option A</td>
                          <td>Option B</td>
                          <td>Option C</td>
                          <td>Option D</td>
                          <td>-</td>
                          <td><strong>b</strong></td>
                          <td>5</td>
                        </tr>
                      </tbody>
                    </table>
                  </div>
                  <div className="guide-section" style={{backgroundColor: '#eff6ff', padding: '12px', borderRadius: '6px', border: '1px solid #bfdbfe'}}>
                    <h4 style={{marginTop: 0}}>💡 Tips</h4>
                    <ul className="guide-list">
                      <li>Kolom E (Opsi E) boleh kosong jika hanya ada 4 opsi</li>
                      <li>Jawaban ditulis dengan huruf kecil: a, b, c, d, atau e</li>
                      <li>Poin harus berupa angka positif</li>
                      <li>Tidak ada baris header di file Excel, langsung data</li>
                    </ul>
                  </div>
                </div>
              ) : (
                <div className="upload-guide">
                  <div className="guide-section">
                    <h4>📋 Format File Excel</h4>
                    <p>File harus berformat <strong>.xlsx</strong> atau <strong>.xls</strong> dengan kolom berikut:</p>
                    <div className="guide-columns">
                      <div className="column-item"><strong>Kolom A</strong><br/>Pertanyaan</div>
                      <div className="column-item"><strong>Kolom B</strong><br/>Rubrik Penilaian</div>
                      <div className="column-item"><strong>Kolom C</strong><br/>Poin Maksimal</div>
                    </div>
                  </div>
                  <div className="guide-section">
                    <h4>📊 Contoh Data</h4>
                    <table className="example-table">
                      <thead>
                        <tr>
                          <th>Pertanyaan</th>
                          <th>Rubrik Penilaian</th>
                          <th>Poin Maksimal</th>
                        </tr>
                      </thead>
                      <tbody>
                        <tr>
                          <td>Jelaskan konsep pemrograman berorientasi objek</td>
                          <td>Jelas & lengkap (OOP): 10 pts, Cukup jelas: 7 pts, Kurang: 3 pts</td>
                          <td>10</td>
                        </tr>
                        <tr>
                          <td>Bagaimana cara mengatasi bug dalam kode?</td>
                          <td>Metode sistematis: 8 pts, Asal mencoba: 4 pts</td>
                          <td>8</td>
                        </tr>
                      </tbody>
                    </table>
                  </div>
                  <div className="guide-section" style={{backgroundColor: '#fef9c3', padding: '12px', borderRadius: '6px', border: '1px solid #fde047'}}>
                    <h4 style={{marginTop: 0}}>💡 Penting untuk Essay</h4>
                    <ul className="guide-list">
                      <li>✅ Soal essay dinilai secara manual oleh recruiter</li>
                      <li>✅ Rubrik penilaian membantu recruiter dalam mengevaluasi jawaban</li>
                      <li>✅ Poin Maksimal adalah skor tertinggi yang bisa dicapai</li>
                      <li>✅ Tidak ada opsi pilihan (A-E) untuk soal essay</li>
                      <li>✅ Tidak ada kolom untuk jawaban benar</li>
                    </ul>
                  </div>
                </div>
              )}
              <div className="form-actions" style={{justifyContent: 'center'}}>
                <button type="button" className="btn-primary" onClick={() => setShowUploadInstructions(false)} style={{maxWidth: '200px'}}>
                  ✓ Saya Sudah Mengerti
                </button>
              </div>
            </div>
          </div>
        )}
        {showExamQuestionsModal && examModalExam && (
          <div className="form-modal" onClick={() => setShowExamQuestionsModal(false)}>
            <div className="form-box large modal-box" onClick={(e) => e.stopPropagation()}>
              <div className="modal-header">
                <div>
                  <h3>Soal Ujian: {examModalExam.title}</h3>
                  <p style={{margin: '8px 0 0', fontSize: '13px', color: 'var(--text-muted)'}}>Tipe: {examModalExam.multiple_choice === 1 ? 'Pilihan Ganda' : 'Essay'} • Posisi: {examModalExam.position === 'all' ? 'Semua Posisi' : examModalExam.position}</p>
                </div>
                <button className="modal-close-btn" onClick={() => setShowExamQuestionsModal(false)}>✕</button>
              </div>
              <div className="modal-body">
                <div className="detail-grid">
                  <div>
                    <p className="detail-label">Durasi</p>
                    <p className="detail-value">{examModalExam.duration_minutes} menit</p>
                  </div>
                  <div>
                    <p className="detail-label">Min Score</p>
                    <p className="detail-value">{examModalExam.passing_grade}%</p>
                  </div>
                  <div>
                    <p className="detail-label">Total Soal</p>
                    <p className="detail-value">{questions.length} soal</p>
                  </div>
                  <div>
                    <p className="detail-label">Show to Kandidat</p>
                    <p className="detail-value">{examModalExam.total > 0 ? Math.min(examModalExam.total, examModalExam.total_questions) : examModalExam.total_questions} soal</p>
                  </div>
                </div>

                <div className="questions-header" style={{padding: '0', marginTop: '20px', justifyContent: 'space-between', alignItems: 'center', display: 'flex', flexWrap: 'wrap', gap: '12px'}}>
                  <div>
                    <h4 style={{margin: '0 0 6px'}}>Manage Soal</h4>
                    <p style={{margin: 0, fontSize: '13px', color: 'var(--text-muted)'}}>Edit, tambah, upload, dan hapus soal terpilih langsung dari modal ini.</p>
                  </div>
                  <div style={{display: 'flex', gap: '10px', flexWrap: 'wrap'}}>
                    <button type="button" className="btn-info" onClick={() => {
                      setUploadInstructionsType(examModalExam.multiple_choice === 1 ? 'multiple-choice' : 'essay');
                      setShowUploadInstructions(true);
                    }}>
                      ℹ️ Panduan Upload
                    </button>
                    <button type="button" className="btn-add-question" onClick={() => {
                      setEditingQuestion(null);
                      setQuestionForm({
                        question_text: '',
                        option_a: '',
                        option_b: '',
                        option_c: '',
                        option_d: '',
                        option_e: '',
                        correct_answer: 'a',
                        points: 10
                      });
                      setShowQuestionForm(true);
                    }}>
                      ➕ Tambah Soal
                    </button>
                    <button className="btn-danger" onClick={bulkDeleteSelectedQuestions}>Hapus Terpilih</button>
                  </div>
                </div>

                <div className="bulk-actions" style={{marginTop: '16px'}}>
                  <label className="upload-label">
                    📤 Upload Excel soal:
                    <input
                      type="file"
                      accept=".xlsx,.xls"
                      onChange={(e) => setExcelFile(e.target.files?.[0] || null)}
                    />
                  </label>
                  <button className="btn-primary" onClick={uploadQuestionsExcelFile}>Upload Excel</button>
                </div>

                <div className="bulk-actions" style={{marginTop: '12px'}}>
                  <div className="bulk-actions-left">
                    <a className="btn-info link-btn" href="/templates/Template_Exam_Pilihan_Ganda.xlsx" download>
                      ⬇️ Template Exam Pilihan Ganda
                    </a>
                    
                    <label className="upload-label">
                      Show List:
                      <select value={questionPerPage} onChange={(e) => setQuestionPerPage(e.target.value)}>
                        <option value="10">10</option>
                        <option value="25">25</option>
                        <option value="50">50</option>
                        <option value="all">All</option>
                      </select>
                    </label>
                    <button type="button" className="btn-info" onClick={exportExamQuestionsExcel}>
                      ⬇️ Export Excel
                    </button>
                  </div>
                </div>

                {showQuestionForm && selectedExamForQuestions === examModalExam.id && (
                  <div className="form-modal">
                    <div className="form-box large">
                      <div className="modal-header">
                        <h3>
                          {editingQuestion ? 'Edit Soal' : 'Soal Baru'}
                          <span style={{fontSize: '13px', fontWeight: 400, color: 'var(--text-muted)', marginLeft: '10px'}}>
                            {examModalExam.multiple_choice === 1 ? '📝 Pilihan Ganda' : '✍️ Essay'}
                          </span>
                        </h3>
                        <button className="modal-close-btn" onClick={() => setShowQuestionForm(false)}>✕</button>
                      </div>
                      <div className="form-group">
                        <label>Pertanyaan</label>
                        <textarea
                          value={questionForm.question_text}
                          onChange={(e) => setQuestionForm({...questionForm, question_text: e.target.value})}
                          placeholder="Masukkan pertanyaan..."
                          rows="3"
                        />
                      </div>
                      {examModalExam.multiple_choice === 1 ? (
                        <>
                          <div className="form-group">
                            <label>Opsi A</label>
                            <input type="text" value={questionForm.option_a} onChange={(e) => setQuestionForm({...questionForm, option_a: e.target.value})} placeholder="Opsi A" />
                          </div>
                          <div className="form-group">
                            <label>Opsi B</label>
                            <input type="text" value={questionForm.option_b} onChange={(e) => setQuestionForm({...questionForm, option_b: e.target.value})} placeholder="Opsi B" />
                          </div>
                          <div className="form-group">
                            <label>Opsi C</label>
                            <input type="text" value={questionForm.option_c} onChange={(e) => setQuestionForm({...questionForm, option_c: e.target.value})} placeholder="Opsi C" />
                          </div>
                          <div className="form-group">
                            <label>Opsi D</label>
                            <input type="text" value={questionForm.option_d} onChange={(e) => setQuestionForm({...questionForm, option_d: e.target.value})} placeholder="Opsi D" />
                          </div>
                          <div className="form-group">
                            <label>Opsi E <span style={{fontWeight:400, color:'var(--text-muted)'}}>(opsional)</span></label>
                            <input type="text" value={questionForm.option_e} onChange={(e) => setQuestionForm({...questionForm, option_e: e.target.value})} placeholder="Opsi E (kosongkan jika tidak ada)" />
                          </div>
                          <div className="form-row">
                            <div className="form-group">
                              <label>Jawaban Benar</label>
                              <select value={questionForm.correct_answer} onChange={(e) => setQuestionForm({...questionForm, correct_answer: e.target.value})}>
                                <option value="a">A</option>
                                <option value="b">B</option>
                                <option value="c">C</option>
                                <option value="d">D</option>
                                <option value="e">E</option>
                              </select>
                            </div>
                            <div className="form-group">
                              <label>Poin</label>
                              <input type="number" value={questionForm.points} onChange={(e) => setQuestionForm({...questionForm, points: parseInt(e.target.value)})} min="1" />
                            </div>
                          </div>
                        </>
                      ) : (
                        <>
                          <div className="essay-info-box">
                            <span>✍️</span>
                            <div>
                              <strong>Soal Essay</strong>
                              <p>Recruiter akan mengoreksi dan memberikan nilai secara manual setelah kandidat mengerjakan ujian.</p>
                            </div>
                          </div>
                          <div className="form-group">
                            <label>Rubrik Penilaian <span style={{fontWeight:400, color:'var(--text-muted)'}}>(panduan untuk recruiter)</span></label>
                            <textarea
                              value={questionForm.option_a}
                              onChange={(e) => setQuestionForm({...questionForm, option_a: e.target.value})}
                              placeholder="Contoh: Jawaban lengkap dan tepat: 100%, Sebagian benar: 50-70%, Tidak relevan: 0%"
                              rows="3"
                            />
                          </div>
                          <div className="form-group">
                            <label>Poin Maksimal</label>
                            <input type="number" value={questionForm.points} onChange={(e) => setQuestionForm({...questionForm, points: parseInt(e.target.value)})} min="1" />
                          </div>
                        </>
                      )}
                      <div className="form-group">
                        <label>Foto Soal <span style={{fontWeight:400, color:'var(--text-muted)'}}>(opsional)</span></label>
                        <input
                          type="file"
                          accept="image/*"
                          onChange={(e) => {
                            const file = e.target.files?.[0] || null;
                            setQuestionPhotoFile(file);
                            if (file) {
                              const reader = new FileReader();
                              reader.onload = () => setQuestionPhotoPreview(reader.result);
                              reader.readAsDataURL(file);
                            } else {
                              setQuestionPhotoPreview(null);
                            }
                          }}
                        />
                        {questionPhotoPreview && (
                          <div className="preview-box">
                            <img src={questionPhotoPreview} alt="Preview soal" />
                          </div>
                        )}
                      </div>
                      <div className="form-actions">
                        <button className="btn-save" onClick={saveQuestion}>Simpan</button>
                        <button className="btn-cancel" onClick={() => setShowQuestionForm(false)}>Batal</button>
                      </div>
                    </div>
                  </div>
                )}

                {loadingQuestions ? (
                  <div className="loading-text">Memuat soal...</div>
                ) : questions.length === 0 ? (
                  <div className="empty-state">Belum ada soal untuk ujian ini.</div>
                ) : (
                  <div className="questions-table-container" style={{marginTop: '16px'}}>
                    <table className="questions-table">
                      <thead>
                        <tr>
                          <th style={{width: '30px'}}>
                            <input
                              type="checkbox"
                              checked={selectAllQuestions}
                              onChange={toggleSelectAllQuestions}
                            />
                          </th>
                          <th style={{width: '50px'}}>No</th>
                          <th>Pertanyaan</th>
                          <th style={{width: '100px'}}>Tipe</th>
                          <th>Opsi & Jawaban</th>
                          <th style={{width: '60px'}}>Poin</th>
                          <th style={{width: '80px'}}>Aksi</th>
                        </tr>
                      </thead>
                      <tbody>
                        {paginatedQuestions.map((q, idx) => (
                          <tr key={q.id} className={selectedQuestionIds.includes(q.id) ? 'selected' : ''}>
                            <td>
                              <input
                                type="checkbox"
                                checked={selectedQuestionIds.includes(q.id)}
                                onChange={(e) => toggleQuestionSelection(q.id, e.target.checked)}
                              />
                            </td>
                            <td>{((questionPage - 1) * effectiveQuestionPerPage) + idx + 1}</td>
                            <td>
                              <div className="td-question">
                                {q.photo && <span className="q-photo-badge">📸</span>}
                                <span className="q-text-preview">{q.question_text.substring(0, 80)}{q.question_text.length > 80 ? '...' : ''}</span>
                              </div>
                            </td>
                            <td>
                              <span className={`badge badge-${q.correct_answer ? 'mc' : 'essay'}`}>
                                {q.correct_answer ? '📝 Pilihan Ganda' : '✍️ Essay'}
                              </span>
                            </td>
                            <td>
                              {q.correct_answer ? (
                                <div className="td-options">
                                  {['a', 'b', 'c', 'd', 'e'].map(opt => {
                                    const value = q[`option_${opt}`];
                                    if (!value) return null;
                                    return (
                                      <div key={opt} className={`option-item ${q.correct_answer === opt ? 'correct-option' : ''}`}>
                                        <strong>{opt.toUpperCase()}:</strong> {value.substring(0, 40)}{value.length > 40 ? '...' : ''}
                                      </div>
                                    );
                                  })}
                                </div>
                              ) : (
                                <div>
                                  <span className="essay-label">✍️ Essay — Recruiter menilai</span>
                                  {q.option_a && (
                                    <div style={{fontSize: '11px', color: 'var(--text-muted)', marginTop: '4px'}}>
                                      📋 Rubrik: {q.option_a.substring(0, 60)}{q.option_a.length > 60 ? '...' : ''}
                                    </div>
                                  )}
                                </div>
                              )}
                            </td>
                            <td><strong>{q.points}</strong></td>
                            <td>
                              <div className="td-actions">
                                <button className="btn-sm btn-secondary" onClick={() => startEditQuestion(q)}>Edit</button>
                                <button className="btn-sm btn-danger" onClick={() => deleteQuestion(q.id)}>Hapus</button>
                              </div>
                            </td>
                          </tr>
                        ))}
                      </tbody>
                    </table>

                    {totalPages > 1 && (
                      <div className="pagination">
                        <button
                          className="pagination-btn"
                          onClick={() => setQuestionPage(Math.max(1, questionPage - 1))}
                          disabled={questionPage === 1}
                        >
                          ← Sebelumnya
                        </button>
                        <div className="pagination-info">
                          Halaman {questionPage} dari {totalPages} (Total: {questions.length} soal)
                        </div>
                        <button
                          className="pagination-btn"
                          onClick={() => setQuestionPage(Math.min(totalPages, questionPage + 1))}
                          disabled={questionPage === totalPages}
                        >
                          Berikutnya →
                        </button>
                      </div>
                    )}
                  </div>
                )}
              </div>
            </div>
          </div>
        )}
      </div>
    </div>
  );
}
